import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, serverTimestamp, addDoc, limit } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { signInAnonymously } from 'firebase/auth';

import { db, storage, auth } from '../config/firebase';
import { useToast } from '../contexts/ToastContext';

import PlannerIcons from '../components/shared/PlannerIcons';
import LargePreviewModal from '../components/ui/LargePreviewModal';
import ImageCropperModal from '../components/ui/ImageCropperModal';
import AiImageGeneratorModal from '../components/ui/AiImageGeneratorModal';
import ImageUpload from '../components/ui/ImageUpload';
import IFramePreview from '../components/ui/IFramePreview';
import SmsComplianceCheckboxes from '../components/ui/SmsComplianceCheckboxes';

// Import planner components
import { WeddingGuestsManager } from '../features/planner/WeddingGuestsManager';
import Checklist from '../features/planner/Checklist';
import BudgetTracker from '../features/planner/BudgetTracker';
import { WeddingRentalsManager } from '../features/planner/WeddingRentalsManager';

// --- SHARED RENDERER COMPONENTS ---

export default function WeddingSiteEditor({ wedding, user, db, storage, navigateTo }) {
    const toast = useToast();
    const [siteData, setSiteData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [activeSectionId, setActiveSectionId] = useState(null);
    const [showAiGenerator, setShowAiGenerator] = useState(false);
    const [showAddSection, setShowAddSection] = useState(false);
    const [templateToApply, setTemplateToApply] = useState(null);
    const [mobileView, setMobileView] = useState('editor');
    const [mobileEditorOpen, setMobileEditorOpen] = useState(false); 
    const [fullScreenPreview, setFullScreenPreview] = useState(false); 
    const [previewDevice, setPreviewDevice] = useState('desktop'); 
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [editorStep, setEditorStep] = useState(1); // NEW: Wizard Step

    // NEW: Hotel Suggestions State
    const [hotelSuggestions, setHotelSuggestions] = useState([]);
    const [isSearchingHotels, setIsSearchingHotels] = useState(false);

    // NEW: Cropping state for existing images
    const [croppingHeroImage, setCroppingHeroImage] = useState(null);

    // NEW: Undo / Redo States
    const [past, setPast] = useState([]);
    const [future, setFuture] = useState([]);

    // NEW: Auto-Save State
    const [isAutoSaving, setIsAutoSaving] = useState(false);
    const autoSaveTimeoutRef = useRef(null);
    const initialLoadRef = useRef(true);

    // NEW: Unified Hub State
    const [hubTab, setHubTab] = useState('website'); 

    // NEW: Scroll preview when active section changes
    useEffect(() => {
        if (activeSectionId && mobileView !== 'editor') {
            const el = document.getElementById(`preview-${activeSectionId}`);
            if (el) {
                setTimeout(() => {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
            }
        }
    }, [activeSectionId, mobileView]);

    // Drag and Drop state
    const dragItem = useRef();
    const dragOverItem = useRef();

    const googleFonts = [
        "Montserrat", 
        "Lato", 
        "Playfair Display", 
        "Roboto", 
        "Dancing Script",
        "Cormorant Garamond",
        "Great Vibes",
        "Cinzel",
        "Alex Brush",
        "Parisienne",
        "Pinyon Script",
        "Lora"
    ];
    const colorPalettes = [
        { name: "Dusty Rose & Sage", primary: "#C08497", background: "#F5F5F5" },
        { name: "Navy & Gold", primary: "#2B3A67", background: "#FDF6E9" },
        { name: "Terracotta & Cream", primary: "#E07A5F", background: "#F4F1DE" },
        { name: "Emerald & Ivory", primary: "#006A4E", background: "#FFFFF0" },
        { name: "Lavender & Silver", primary: "#9B89B3", background: "#EAEAEA" },
        { name: "Champagne & Black", primary: "#333333", background: "#F7E7CE" },
    ];

    useEffect(() => {
        if (!wedding || !wedding.weddingId) {
            setError("Invalid wedding ID.");
            setLoading(false);
            return;
        }
        
        const fetchSite = async () => {
            try {
                const docRef = doc(db, "weddings", wedding.weddingId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = { id: docSnap.id, ...docSnap.data() };
                    setSiteData(data);
                    if (data.sections && data.sections.length > 0) {
                        setActiveSectionId(data.sections[0].id);
                    }
                } else {
                    setError("Wedding website not found.");
                }
            } catch (err) {
                console.error("Error fetching wedding site:", err);
                setError("Failed to load editor.");
            } finally {
                setLoading(false);
            }
        };
        
        fetchSite();
    }, [wedding, db]);

    const weddingTemplates = [
        {
            id: 'modern-romance',
            name: 'Modern Romance',
            theme: { primaryColor: '#f472b6', backgroundColor: '#fffbfb', fontFamily: "Lato, sans-serif" },
            sections: [
                { id: 'hero_1', type: 'hero', enabled: true, content: { title: "Sarah & James", date: "2026-09-12", imageUrl: "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1920&q=80" } },
                { id: 'story_1', type: 'story', enabled: true, content: { heading: "Our Story", text: "We met in the city, fell in love by the ocean, and can't wait to spend forever together.", imageUrl: "" } },
                { id: 'events_1', type: 'events', enabled: true, content: { heading: "The Details", eventsList: [{ title: 'Ceremony', time: '16:00', location: 'Botanical Gardens', description: 'Join us as we say I do.' }, { title: 'Reception', time: '18:00', location: 'The Grand Hall', description: 'Dinner, drinks, and dancing.' }] } },
                { id: 'rsvp_1', type: 'rsvp', enabled: true, content: { heading: "Will you join us?" } }
            ]
        },
        {
            id: 'classic-elegance',
            name: 'Classic Elegance',
            theme: { primaryColor: '#1e3a8a', backgroundColor: '#f8fafc', fontFamily: "Playfair Display, serif" },
            sections: [
                { id: 'hero_2', type: 'hero', enabled: true, content: { title: "William & Victoria", date: "2026-11-07", imageUrl: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=1920&q=80" } },
                { id: 'story_2', type: 'story', enabled: true, content: { heading: "How It Began", text: "A classic tale of romance. Two lives, intertwined forever.", imageUrl: "" } },
                { id: 'travel_2', type: 'travel', enabled: true, content: { heading: "Accommodations", accommodations: [{name: "The Grand Hotel", address: "Downtown Plaza", details: "Please mention our names for a discounted room block rate.", bookingUrl: ""}] } },
                { id: 'registry_2', type: 'registry', enabled: true, content: { heading: "Gift Registry", links: [{ storeName: 'Our Honeyfund', url: '#' }] } },
                { id: 'rsvp_2', type: 'rsvp', enabled: true, content: { heading: "RSVP" } }
            ]
        },
        {
            id: 'rustic-charm',
            name: 'Rustic Charm',
            theme: { primaryColor: '#bc6c25', backgroundColor: '#fefae0', fontFamily: "Georgia, serif" },
            sections: [
                { id: 'hero_3', type: 'hero', enabled: true, content: { title: "Emma & Noah", date: "2026-10-24", imageUrl: "https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?auto=format&fit=crop&w=1920&q=80" } },
                { id: 'story_3', type: 'story', enabled: true, content: { heading: "Our Journey", text: "From the first hike to the proposal under the stars.", imageUrl: "" } },
                { id: 'gallery_3', type: 'gallery', enabled: true, content: { heading: "Captured Moments", images: [] } },
                { id: 'rsvp_3', type: 'rsvp', enabled: true, content: { heading: "Save Your Seat" } }
            ]
        },
        {
            id: 'minimalist-editorial',
            name: 'Minimalist Editorial',
            theme: { primaryColor: '#111827', backgroundColor: '#ffffff', fontFamily: "Montserrat, sans-serif" },
            sections: [
                { id: 'hero_4', type: 'hero', enabled: true, content: { title: "A & B", subtitle: "Are tying the knot", date: "2026-12-31", imageUrl: "https://images.unsplash.com/photo-1515934751635-c81c6bc9a2d8?auto=format&fit=crop&w=1920&q=80", buttonText: "RSVP", alignment: 'center', overlayOpacity: 0.2, height: '100vh', cardStyle: 'transparent', textColor: '#111827' } },
                { id: 'story_4', type: 'story', enabled: true, content: { heading: "The Details", text: "Join us for an evening of celebration, fine dining, and endless dancing in the city.", imageUrl: "", textColor: "#111827", layout: "left" } },
                { id: 'events_4', type: 'events', enabled: true, content: { heading: "Schedule", eventsList: [{ title: 'Ceremony', time: '5:00 PM', location: 'The Loft', description: 'Black tie optional.' }, { title: 'Reception', time: '6:30 PM', location: 'Main Gallery', description: 'Dinner served at 7.' }] } },
                { id: 'rsvp_4', type: 'rsvp', enabled: true, content: { heading: "RSVP", bgColor: "#f9fafb", textColor: "#111827" } }
            ]
        },
        {
            id: 'botanical-garden',
            name: 'Botanical Garden',
            theme: { primaryColor: '#4a5d23', backgroundColor: '#fcfdfc', fontFamily: "Cormorant Garamond, serif" },
            sections: [
                { id: 'hero_5', type: 'hero', enabled: true, content: { title: "Sophia & Liam", subtitle: "We are getting married", date: "2027-05-15", imageUrl: "https://images.unsplash.com/photo-1469334031218-e382a71b716b?auto=format&fit=crop&w=1920&q=80", buttonText: "Join Us", alignment: 'center', overlayOpacity: 0.4, height: '600px', cardStyle: 'solid' } },
                { id: 'story_5', type: 'story', enabled: true, content: { heading: "Our Story", text: "We can't wait to celebrate our special day with you surrounded by nature and love.", imageUrl: "https://images.unsplash.com/photo-1520854221256-17451cc331bf?auto=format&fit=crop&w=800&q=80", layout: "left" } },
                { id: 'travel_5', type: 'travel', enabled: true, content: { heading: "Travel & Stay", accommodations: [{name: "Garden Inn", address: "100 Floral Way", details: "A shuttle will be provided for guests staying at this hotel.", bookingUrl: ""}] } },
                { id: 'registry_5', type: 'registry', enabled: true, content: { heading: "Registry", links: [{ storeName: 'Crate & Barrel', url: '#' }] } },
                { id: 'rsvp_5', type: 'rsvp', enabled: true, content: { heading: "Kindly Reply" } }
            ]
        },
        {
            id: 'moody-romantic',
            name: 'Moody Romantic',
            theme: { primaryColor: '#e0a96d', backgroundColor: '#262626', fontFamily: "Playfair Display, serif" },
            sections: [
                { id: 'hero_6', type: 'hero', enabled: true, content: { title: "Mia & Ethan", subtitle: "A night to remember", date: "2026-10-31", imageUrl: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=1920&q=80", buttonText: "RSVP", alignment: 'center', overlayOpacity: 0.7, height: '800px', cardStyle: 'transparent', textColor: '#ffffff' } },
                { id: 'story_6', type: 'story', enabled: true, content: { heading: "Join Us", text: "Under the stars, we will say our vows. We look forward to sharing this magical evening with our closest friends and family.", imageUrl: "", textColor: "#ffffff" } },
                { id: 'gallery_6', type: 'gallery', enabled: true, content: { heading: "Moments", images: [], textColor: "#e0a96d" } },
                { id: 'rsvp_6', type: 'rsvp', enabled: true, content: { heading: "Will You Be There?", bgColor: "#1a1a1a", textColor: "#ffffff" } }
            ]
        }
    ];

    useEffect(() => {
        if (!siteData) return;
        
        const fontsToLoad = new Set();
        if (siteData.theme?.fontFamily) fontsToLoad.add(siteData.theme.fontFamily);
        (siteData.sections || []).forEach(sec => {
            if (sec.content?.fontFamily) fontsToLoad.add(sec.content.fontFamily);
        });

        fontsToLoad.forEach(fontFamily => {
            const font = fontFamily.replace(/'/g, "").split(',')[0].trim().replace(/ /g, "+");
            if (!font) return;
            const linkId = `google-font-${font.toLowerCase()}`;
            let link = document.getElementById(linkId);
            if (!link) {
                link = document.createElement('link');
                link.id = linkId;
                link.rel = 'stylesheet';
                link.href = `https://fonts.googleapis.com/css?family=${font}:400,700&display=swap`;
                document.head.appendChild(link);
            }
        });
    }, [siteData?.theme?.fontFamily, siteData?.sections]);

    // NEW: History Control Helpers
    const saveHistory = () => {
        if (siteData) {
            // Deep copy to prevent mutations from breaking history state, while preserving complex root objects
            const snap = JSON.parse(JSON.stringify(siteData));
            if (siteData.createdAt) snap.createdAt = siteData.createdAt;
            if (siteData.updatedAt) snap.updatedAt = siteData.updatedAt;
            setPast(prev => [...prev, snap]);
            setFuture([]);
        }
    };

    const handleUndo = () => {
        if (past.length === 0) return;
        const previous = past[past.length - 1];
        setFuture(prev => [siteData, ...prev]);
        setPast(prev => prev.slice(0, -1));
        setSiteData(previous);
    };

    const handleRedo = () => {
        if (future.length === 0) return;
        const next = future[0];
        setPast(prev => [...prev, siteData]);
        setFuture(prev => prev.slice(1));
        setSiteData(next);
    };

    // NEW: Auto-Save Effect
    useEffect(() => {
        if (!siteData) return;
        
        // Prevent auto-saving immediately on the initial data load
        if (initialLoadRef.current) {
            initialLoadRef.current = false;
            return;
        }
        
        if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
        
        // Debounce saves by 3 seconds of inactivity
        autoSaveTimeoutRef.current = setTimeout(async () => {
            setIsAutoSaving(true);
            try {
                await updateDoc(doc(db, "weddings", siteData.id), {
                    ...siteData,
                    updatedAt: serverTimestamp()
                });
            } catch (err) {
                console.error("Auto-save error:", err);
            } finally {
                setIsAutoSaving(false);
            }
        }, 3000);
        
        return () => clearTimeout(autoSaveTimeoutRef.current);
    }, [siteData, db]);

    const handleFieldChange = (sectionId, field, value) => {
        saveHistory();
        setSiteData(prev => {
            const newSections = prev.sections.map(sec => {
                if (sec.id === sectionId) {
                    return { ...sec, content: { ...sec.content, [field]: value } };
                }
                return sec;
            });
            return { ...prev, sections: newSections };
        });
    };

    const handleTemplateClick = (template) => {
        setTemplateToApply(template);
    };

    const confirmApplyTemplate = () => {
        if (!templateToApply) return;
        saveHistory();
        
        const existingSections = [...siteData.sections];
        const templateSections = JSON.parse(JSON.stringify(templateToApply.sections));

        // Group existing sections by type
        const existingByType = {};
        existingSections.forEach(sec => {
            if (!existingByType[sec.type]) existingByType[sec.type] = [];
            existingByType[sec.type].push(sec);
        });

        const newSections = [];
        
        // 1. Process template sections, injecting existing content where applicable
        templateSections.forEach(tplSec => {
            if (existingByType[tplSec.type] && existingByType[tplSec.type].length > 0) {
                const existing = existingByType[tplSec.type].shift();
                const mergedContent = { ...tplSec.content };
                
                // Preserve user's text and data
                const dataFields = ['title', 'date', 'heading', 'text', 'eventsList', 'accommodations', 'links', 'images', 'buttonText'];
                dataFields.forEach(field => {
                    if (existing.content[field] !== undefined && existing.content[field] !== '') {
                        mergedContent[field] = existing.content[field];
                    }
                });
                
                // Preserve custom images (ignore placeholders)
                if (existing.content.imageUrl && !existing.content.imageUrl.includes('placehold.co')) {
                    mergedContent.imageUrl = existing.content.imageUrl;
                }

                newSections.push({
                    ...tplSec,
                    id: existing.id, // Preserve original ID
                    content: mergedContent,
                    enabled: existing.enabled
                });
            } else {
                newSections.push(tplSec);
            }
        });

        // 2. Append any existing sections that weren't in the new template so data isn't lost
        Object.values(existingByType).forEach(leftoverSections => {
            leftoverSections.forEach(leftover => {
                const cleanContent = { ...leftover.content };
                // Strip manual style overrides so it inherits the new theme cleanly
                delete cleanContent.bgColor;
                delete cleanContent.textColor;
                delete cleanContent.fontFamily;
                
                newSections.push({
                    ...leftover,
                    content: cleanContent
                });
            });
        });

        setSiteData(prev => ({
            ...prev,
            theme: templateToApply.theme,
            sections: newSections
        }));
        setActiveSectionId(newSections[0].id);
        setTemplateToApply(null);
    };

    const moveSection = (index, direction) => {
        saveHistory();
        const newSections = [...siteData.sections];
        if (direction === 'up' && index > 0) {
            [newSections[index], newSections[index - 1]] = [newSections[index - 1], newSections[index]];
        } else if (direction === 'down' && index < newSections.length - 1) {
            [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
        }
        setSiteData(prev => ({ ...prev, sections: newSections }));
    };

    const toggleSection = (id) => {
        saveHistory();
        setSiteData(prev => ({
            ...prev,
            sections: prev.sections.map(sec => sec.id === id ? { ...sec, enabled: !sec.enabled } : sec)
        }));
    };

    const handleDrop = () => {
        saveHistory();
        const newSections = [...siteData.sections];
        const dragItemContent = newSections.splice(dragItem.current, 1)[0];
        newSections.splice(dragOverItem.current, 0, dragItemContent);
        dragItem.current = null;
        dragOverItem.current = null;
        setSiteData(prev => ({ ...prev, sections: newSections }));
    };

    const duplicateSection = (id) => {
        saveHistory();
        const secToDup = siteData.sections.find(s => s.id === id);
        if (!secToDup) return;
        const newSec = JSON.parse(JSON.stringify(secToDup));
        newSec.id = `${newSec.type}_${Date.now()}`;
        const idx = siteData.sections.findIndex(s => s.id === id);
        const newSections = [...siteData.sections];
        newSections.splice(idx + 1, 0, newSec);
        setSiteData(prev => ({ ...prev, sections: newSections }));
        setActiveSectionId(newSec.id);
    };

    const deleteSection = (id) => {
        if (siteData.sections.length <= 1) {
            toast.error("You must have at least one section.");
            return;
        }
        if (window.confirm("Are you sure you want to delete this block?")) {
            saveHistory();
            const newSections = siteData.sections.filter(s => s.id !== id);
            setSiteData(prev => ({ ...prev, sections: newSections }));
            if (activeSectionId === id) setActiveSectionId(newSections[0].id);
        }
    };

    const addSection = (type) => {
        saveHistory();
        const newSection = {
            id: `section_${Date.now()}`,
            type: type,
            enabled: true,
            content: {}
        };
        switch (type) {
            case 'hero': newSection.content = { title: 'Welcome', date: '', city: '', imageUrl: '' }; break;
            case 'story': newSection.content = { heading: 'Our Story', text: 'It all started...', imageUrl: '' }; break;
            case 'events': newSection.content = { heading: 'Schedule of Events', eventsList: [{ title: 'New Event', time: '', location: '', description: '' }] }; break;
            case 'rsvp': newSection.content = { heading: 'Will You Be There?' }; break;
            case 'gallery': newSection.content = { heading: 'Our Gallery', images: [] }; break;
            case 'registry': newSection.content = { heading: 'Gift Registry', links: [{ storeName: '', url: '' }] }; break;
            case 'travel': newSection.content = { heading: 'Travel & Accommodations', accommodations: [{ name: '', address: '', details: '', bookingUrl: '' }] }; break;
            default: break;
        }
        setSiteData(prev => ({ ...prev, sections: [...prev.sections, newSection] }));
        setActiveSectionId(newSection.id);
        setMobileView('preview');
        setShowAddSection(false);
    };

    const handlePublish = async () => {
        if (!siteData.subdomain || siteData.subdomain.trim() === '') {
            toast.error("Please set a unique URL path for your site before publishing.");
            return;
        }
        
        try {
            // NEW: Subdomain uniqueness check before publishing
            const q = query(collection(db, 'weddings'), where('subdomain', '==', siteData.subdomain));
            const snapshot = await getDocs(q);
            const isDuplicate = snapshot.docs.some(docSnap => docSnap.id !== siteData.id);
            
            if (isDuplicate) {
                toast.error('This website URL is already taken. Please choose a different one.');
                return;
            }

            await updateDoc(doc(db, "weddings", siteData.id), { isPublished: !siteData.isPublished });
            toast.success(siteData.isPublished ? "Website unpublished." : "Website is now live!");
        } catch (err) {
            console.error("Error publishing:", err);
            toast.error("Failed to update publish status.");
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // NEW: Subdomain uniqueness check before saving
            if (siteData.subdomain) {
                const q = query(collection(db, 'weddings'), where('subdomain', '==', siteData.subdomain));
                const snapshot = await getDocs(q);
                const isDuplicate = snapshot.docs.some(docSnap => docSnap.id !== siteData.id);
                
                if (isDuplicate) {
                    toast.error('This website URL is already taken. Please choose a different one.');
                    setSaving(false);
                    return;
                }
            }

            await updateDoc(doc(db, "weddings", siteData.id), siteData);
            toast.success("Changes saved!");
        } catch (err) {
            console.error(err);
            toast.error("Error saving site.");
        } finally {
            setSaving(false);
        }
    };

    const handleAiImageUpload = async (imageBlob) => {
        if (!user) return;
        setSaving(true);
        try {
            const filePath = `wedding-sites/${siteData.id}/${user.uid}/${Date.now()}_ai.jpg`;
            const fileRef = ref(storage, filePath);
            await uploadBytes(fileRef, imageBlob);
            const url = await getDownloadURL(fileRef);
            handleFieldChange(activeSectionId, 'imageUrl', url);
        } catch (error) {
            console.error("AI Image upload failed:", error);
            toast.error("Failed to upload the generated image.");
        } finally {
            setSaving(false);
        }
    };

    const handleFindHotels = (sectionId) => {
        if (!window.google || !window.google.maps) {
            toast.error("Google Maps is not loaded yet. Please try again in a moment.");
            return;
        }
        
        const heroSection = siteData.sections.find(s => s.type === 'hero');
        let address = heroSection?.content?.city;

        if (!address || address.trim() === '') {
            address = window.prompt("Enter the city or address of your wedding to find nearby hotels:");
            if (!address) return;
        }

        setIsSearchingHotels(true);
        setHotelSuggestions([]);
        
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address }, (results, status) => {
            if (status === 'OK' && results[0]) {
                const loc = results[0].geometry.location;
                const mapDiv = document.createElement('div');
                const service = new window.google.maps.places.PlacesService(mapDiv);
                
                const request = {
                    location: loc,
                    radius: '15000',
                    type: ['lodging']
                };

                service.nearbySearch(request, (places, searchStatus) => {
                    if (searchStatus === window.google.maps.places.PlacesServiceStatus.OK && places) {
                        const sortedPlaces = places.sort((a, b) => (b.rating || 0) - (a.rating || 0));
                        const suggestions = sortedPlaces.slice(0, 8).map(p => ({
                            name: p.name,
                            address: p.vicinity,
                            rating: p.rating,
                            reviews: p.user_ratings_total,
                            priceLevel: p.price_level,
                            photoUrl: p.photos && p.photos.length > 0 ? p.photos[0].getUrl({ maxWidth: 600 }) : 'https://placehold.co/600x400/e2e8f0/64748b?text=No+Image',
                            url: `https://www.google.com/travel/hotels/search?q=${encodeURIComponent(p.name + ' ' + p.vicinity)}`
                        }));
                        setHotelSuggestions(suggestions);
                    } else {
                        toast.error("No hotels found nearby.");
                    }
                    setIsSearchingHotels(false);
                });
            } else {
                toast.error("Could not locate the event address on the map.");
                setIsSearchingHotels(false);
            }
        });
    };

    const handleHeroCropComplete = async (blob) => {
        setCroppingHeroImage(null);
        if (!blob || !storage || !user) return;
        setSaving(true);
        try {
            const file = new File([blob], `hero_crop_${Date.now()}.jpg`, { type: 'image/jpeg' });
            const filePath = `wedding-sites/${siteData.id}/${user.uid}/${file.name}`;
            const fileRef = ref(storage, filePath);
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);
            handleFieldChange(activeSectionId, 'imageUrl', url);
            toast.success("Image cropped successfully!");
        } catch (err) {
            console.error("Crop upload failed:", err);
            toast.error("Failed to save cropped image.");
        } finally {
            setSaving(false);
        }
    };

    const renderSectionEditor = (section) => {
        if (!section || section.id !== activeSectionId) return null;

        const renderHeader = (title) => (
            <div className="flex flex-col gap-3 border-b border-gray-200 pb-3 mb-4">
                <div className="flex justify-between items-center">
                    <h4 className="font-bold text-lg text-gray-900">{title}</h4>
                    <label className="flex items-center text-sm gap-2 cursor-pointer bg-gray-100 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition">
                        <input type="checkbox" checked={section.enabled} onChange={() => toggleSection(section.id)} className="w-4 h-4 text-pink-600 rounded focus:ring-pink-500" />
                        <span className="font-semibold text-gray-700">Visible</span>
                    </label>
                </div>
                <label className="flex items-center text-xs gap-2 cursor-pointer self-start">
                    <input type="checkbox" checked={!section.hideFromNav} onChange={(e) => handleFieldChange(section.id, 'hideFromNav', !e.target.checked)} className="w-3.5 h-3.5 text-blue-600 rounded focus:ring-blue-500" />
                    <span className="font-medium text-gray-600">Show in Navigation Menu</span>
                </label>
            </div>
        );

        // NEW: Section Styling Overrides Helper
        const renderSectionStylesOverrides = () => (
            <div className="border-t border-gray-100 pt-4 mt-4 space-y-4">
                <h5 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Section Styling Overrides</h5>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Background Color</label>
                        <div className="flex items-center gap-2">
                            <input type="color" value={section.content.bgColor || siteData.theme.backgroundColor || '#ffffff'} onChange={e => handleFieldChange(section.id, 'bgColor', e.target.value)} className="w-8 h-8 p-0 border-0 rounded cursor-pointer shadow-sm" />
                            <button onClick={() => handleFieldChange(section.id, 'bgColor', '')} className="text-[10px] font-bold text-gray-400 hover:text-red-500 uppercase tracking-wider transition">Clear</button>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Text Color</label>
                        <div className="flex items-center gap-2">
                            <input type="color" value={section.content.textColor || '#000000'} onChange={e => handleFieldChange(section.id, 'textColor', e.target.value)} className="w-8 h-8 p-0 border-0 rounded cursor-pointer shadow-sm" />
                            <button onClick={() => handleFieldChange(section.id, 'textColor', '')} className="text-[10px] font-bold text-gray-400 hover:text-red-500 uppercase tracking-wider transition">Clear</button>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Font Family</label>
                        <select value={section.content.fontFamily || ''} onChange={e => handleFieldChange(section.id, 'fontFamily', e.target.value)} className="w-full p-1.5 border border-gray-300 rounded text-xs bg-white outline-none focus:ring-1 focus:ring-pink-500">
                            <option value="">Theme Default</option>
                            {googleFonts.map(font => <option key={font} value={font}>{font}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Text Size</label>
                        <select value={section.content.textSize || 'normal'} onChange={e => handleFieldChange(section.id, 'textSize', e.target.value)} className="w-full p-1.5 border border-gray-300 rounded text-xs bg-white outline-none focus:ring-1 focus:ring-pink-500">
                            <option value="small">Small</option>
                            <option value="normal">Normal (Default)</option>
                            <option value="large">Large</option>
                            <option value="xlarge">Extra Large</option>
                        </select>
                    </div>
                </div>
            </div>
        );

        switch (section.type) {
            case 'hero':
                return (
                    <div className="p-5 space-y-4 animate-fade-in">
                        {renderHeader('Hero Section')}
                        <div>
                            <ImageUpload weddingId={siteData.id} onUploadComplete={(url) => handleFieldChange(section.id, 'imageUrl', url)} onShowAiGenerator={() => setShowAiGenerator(true)} user={user} />
                        </div>
                        
                        {/* NEW: Text Position Grid */}
                        <div className="border-t border-gray-100 pt-4 mt-2">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-2">Text Position</label>
                            <div className="bg-gray-50 p-2.5 rounded-xl inline-block border border-gray-200 shadow-inner">
                                <div className="grid grid-cols-3 gap-1">
                                    {['top-left', 'top-center', 'top-right', 'center-left', 'center-center', 'center-right', 'bottom-left', 'bottom-center', 'bottom-right'].map(pos => {
                                        const [v, h] = pos.split('-');
                                        const isActive = (section.content.verticalAlign || 'center') === v && (section.content.horizontalAlign || 'center') === h;
                                        return (
                                            <button
                                                key={pos}
                                                onClick={() => {
                                                    handleFieldChange(section.id, 'verticalAlign', v);
                                                    handleFieldChange(section.id, 'horizontalAlign', h);
                                                }}
                                                className={`w-10 h-10 rounded-md border flex items-center justify-center transition-all ${isActive ? 'bg-pink-500 border-pink-600 shadow-md transform scale-105 z-10' : 'bg-white border-gray-300 hover:bg-gray-100'}`}
                                                title={pos.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            >
                                                <div className={`w-3 h-3 rounded-sm ${isActive ? 'bg-white shadow-sm' : 'bg-gray-300'}`}></div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* NEW: Overlay Customization Controls */}
                        <div className="border-t border-gray-100 pt-4 mt-4">
                            <h5 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-3">Hero Features</h5>
                            <label className="flex items-center gap-2 cursor-pointer mb-4">
                                <input type="checkbox" checked={section.content.showCountdown || false} onChange={e => handleFieldChange(section.id, 'showCountdown', e.target.checked)} className="w-4 h-4 text-pink-600 rounded focus:ring-pink-500" />
                                <span className="text-sm font-bold text-gray-700">Show Countdown Timer</span>
                            </label>

                            <h5 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-3">Background Settings</h5>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Tint Color</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" value={section.content.overlayColor || '#000000'} onChange={e => handleFieldChange(section.id, 'overlayColor', e.target.value)} className="w-8 h-8 p-0 border-0 rounded cursor-pointer shadow-sm" />
                                        <button onClick={() => handleFieldChange(section.id, 'overlayColor', '#000000')} className="text-[10px] font-bold text-gray-400 hover:text-red-500 uppercase tracking-wider transition">Reset</button>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Opacity</label>
                                    <div className="flex items-center gap-2 h-8">
                                        <input type="range" min="0" max="1" step="0.05" value={section.content.overlayOpacity !== undefined ? section.content.overlayOpacity : 0.4} onChange={e => handleFieldChange(section.id, 'overlayOpacity', parseFloat(e.target.value))} className="w-full cursor-pointer" />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Card Backdrop Style</label>
                                <select 
                                    value={section.content.cardStyle || 'glass'} 
                                    onChange={e => handleFieldChange(section.id, 'cardStyle', e.target.value)} 
                                    className="w-full p-2 border border-gray-300 rounded text-sm bg-white outline-none focus:ring-1 focus:ring-pink-500"
                                >
                                    <option value="glass">Frosted Glass</option>
                                    <option value="solid">Solid White</option>
                                    <option value="transparent">Transparent (Hidden)</option>
                                </select>
                            </div>
                        </div>

                        {renderSectionStylesOverrides()}
                    </div>
                );
            case 'story':
                return (
                    <div className="p-5 space-y-4 animate-fade-in">
                        {renderHeader('Our Story')}
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Heading</label><input type="text" value={section.content.heading || ''} onChange={e => handleFieldChange(section.id, 'heading', e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg mt-1 focus:ring-2 focus:ring-pink-500 outline-none" /></div>
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Story Text</label><textarea value={section.content.text || ''} onChange={e => handleFieldChange(section.id, 'text', e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg mt-1 h-32 resize-none focus:ring-2 focus:ring-pink-500 outline-none" /></div>
                        <div className="border-t border-gray-100 pt-4">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 block">Story Image (Optional)</label>
                            <ImageUpload weddingId={siteData.id} user={user} onUploadComplete={(url) => handleFieldChange(section.id, 'imageUrl', url)} onShowAiGenerator={() => setShowAiGenerator(true)} />
                            {section.content.imageUrl && (
                                <div className="mt-3 relative inline-block group">
                                    <img src={section.content.imageUrl} alt="Story" className="w-32 h-32 object-cover rounded-xl shadow-md border border-gray-200" />
                                    <button onClick={() => handleFieldChange(section.id, 'imageUrl', '')} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition shadow-lg">✕</button>
                                </div>
                            )}
                        </div>
                        {renderSectionStylesOverrides()}
                    </div>
                );
            case 'events':
                return (
                    <div className="p-5 space-y-4 animate-fade-in">
                        {renderHeader('Schedule of Events')}
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Section Heading</label><input type="text" value={section.content.heading || ''} onChange={e => handleFieldChange(section.id, 'heading', e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg mt-1 focus:ring-2 focus:ring-pink-500 outline-none" /></div>
                        <div className="space-y-4 border-t border-gray-100 pt-4">
                            <label className="text-sm font-bold text-gray-900 block">Manage Events</label>
                            {(section.content.eventsList || []).map((evt, idx) => (
                                <div key={idx} className="bg-gray-50 p-4 rounded-xl border border-gray-200 relative group">
                                    <button onClick={() => {
                                        const newList = [...section.content.eventsList];
                                        newList.splice(idx, 1);
                                        handleFieldChange(section.id, 'eventsList', newList);
                                    }} className="absolute top-2 right-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition p-1 font-bold">✕</button>
                                    <div className="grid grid-cols-2 gap-3 mb-3 pr-6">
                                        <input type="text" placeholder="Name (e.g. Ceremony)" value={evt.title || ''} onChange={e => { const newList = [...section.content.eventsList]; newList[idx].title = e.target.value; handleFieldChange(section.id, 'eventsList', newList); }} className="p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none" />
                                        <input type="text" placeholder="Time (e.g. 4:00 PM)" value={evt.time || ''} onChange={e => { const newList = [...section.content.eventsList]; newList[idx].time = e.target.value; handleFieldChange(section.id, 'eventsList', newList); }} className="p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none" />
                                    </div>
                                    <input type="text" placeholder="Location" value={evt.location || ''} onChange={e => { const newList = [...section.content.eventsList]; newList[idx].location = e.target.value; handleFieldChange(section.id, 'eventsList', newList); }} className="w-full p-2 border border-gray-300 rounded-lg text-sm mb-3 focus:ring-2 focus:ring-pink-500 outline-none" />
                                    <textarea placeholder="Details (Dress code, parking, etc.)" value={evt.description || ''} onChange={e => { const newList = [...section.content.eventsList]; newList[idx].description = e.target.value; handleFieldChange(section.id, 'eventsList', newList); }} className="w-full p-2 border border-gray-300 rounded-lg text-sm resize-none h-20 focus:ring-2 focus:ring-pink-500 outline-none" />
                                </div>
                            ))}
                            <button onClick={() => handleFieldChange(section.id, 'eventsList', [...(section.content.eventsList || []), { title: '', time: '', location: '', description: '' }])} className="w-full py-3 bg-white border-2 border-dashed border-gray-300 rounded-xl font-bold text-gray-500 hover:text-pink-600 hover:border-pink-300 transition">
                                + Add Event
                            </button>
                        </div>
                        {renderSectionStylesOverrides()}
                    </div>
                );
            case 'travel':
                return (
                    <div className="p-5 space-y-4 animate-fade-in">
                        {renderHeader('Travel & Accommodations')}
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Section Heading</label><input type="text" value={section.content.heading || ''} onChange={e => handleFieldChange(section.id, 'heading', e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg mt-1 focus:ring-2 focus:ring-pink-500 outline-none" /></div>
                        <div className="space-y-4 border-t border-gray-100 pt-4">
                            <div className="flex justify-between items-center mb-2">
                                <label className="text-sm font-bold text-gray-900 block">Hotels & Info</label>
                                <button 
                                    onClick={() => handleFindHotels(section.id)}
                                    disabled={isSearchingHotels}
                                    className="text-xs bg-blue-600 hover:bg-blue-700 text-white font-bold px-3 py-1.5 rounded-lg transition flex items-center gap-1.5 shadow-sm disabled:opacity-50"
                                >
                                    {isSearchingHotels ? <span className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span> : '🏨'} 
                                    {isSearchingHotels ? 'Searching...' : 'Find Nearby Stays'}
                                </button>
                            </div>

                            {hotelSuggestions.length > 0 && (
                                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 mb-4 animate-fade-in">
                                    <div className="flex justify-between items-center mb-3">
                                        <h4 className="font-bold text-gray-900 text-sm flex items-center gap-1.5"><span>✨</span> Top Local Stays</h4>
                                        <button onClick={() => setHotelSuggestions([])} className="text-[10px] font-bold text-gray-400 hover:text-red-500 uppercase tracking-widest bg-white px-2 py-1 rounded-md border border-gray-200 shadow-sm transition">Close</button>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-80 overflow-y-auto pr-1">
                                        {hotelSuggestions.map((hotel, idx) => (
                                            <div key={idx} className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200 flex flex-col">
                                                <div className="h-24 bg-gray-200 relative overflow-hidden">
                                                    <img src={hotel.photoUrl} alt={hotel.name} className="w-full h-full object-cover" />
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                                                    {hotel.priceLevel !== undefined && (
                                                        <div className="absolute top-1.5 right-1.5 bg-black/60 backdrop-blur-md text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm">
                                                            {Array(hotel.priceLevel).fill('$').join('')}
                                                        </div>
                                                    )}
                                                    <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1 bg-white/90 px-1.5 py-0.5 rounded shadow-sm">
                                                        <span className="text-yellow-500 text-[10px]">★</span>
                                                        <span className="text-[10px] font-bold text-gray-900">{hotel.rating || 'New'}</span>
                                                    </div>
                                                </div>
                                                <div className="p-2.5 flex flex-col flex-grow">
                                                    <h5 className="font-bold text-gray-900 text-xs mb-0.5 truncate" title={hotel.name}>{hotel.name}</h5>
                                                    <p className="text-[10px] text-gray-500 truncate mb-2">{hotel.address}</p>
                                                    <div className="mt-auto flex flex-col gap-1.5 pt-2 border-t border-gray-100">
                                                        <a href={hotel.url} target="_blank" rel="noopener noreferrer" className="text-center text-[10px] font-bold text-blue-600 bg-blue-50 py-1.5 rounded hover:bg-blue-100 transition">Check Prices ↗</a>
                                                        <button 
                                                            onClick={() => {
                                                                const newList = [...(section.content.accommodations || []), { name: hotel.name, address: hotel.address, details: '', bookingUrl: hotel.url }];
                                                                handleFieldChange(section.id, 'accommodations', newList);
                                                                toast.success("Added to travel list!");
                                                            }}
                                                            className="text-center text-[10px] font-bold text-gray-700 bg-white border border-gray-200 py-1.5 rounded hover:bg-gray-50 transition shadow-sm"
                                                        >
                                                            Add to Site
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {(section.content.accommodations || []).map((acc, idx) => (
                                <div key={idx} className="bg-gray-50 p-4 rounded-xl border border-gray-200 relative group">
                                    <button onClick={() => {
                                        const newList = [...section.content.accommodations];
                                        newList.splice(idx, 1);
                                        handleFieldChange(section.id, 'accommodations', newList);
                                    }} className="absolute top-2 right-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition p-1 font-bold">✕</button>
                                    <input type="text" placeholder="Hotel / Place Name" value={acc.name || ''} onChange={e => { const newList = [...section.content.accommodations]; newList[idx].name = e.target.value; handleFieldChange(section.id, 'accommodations', newList); }} className="w-full p-2 border border-gray-300 rounded-lg text-sm mb-3 focus:ring-2 focus:ring-pink-500 outline-none pr-8" />
                                    <input type="text" placeholder="Address" value={acc.address || ''} onChange={e => { const newList = [...section.content.accommodations]; newList[idx].address = e.target.value; handleFieldChange(section.id, 'accommodations', newList); }} className="w-full p-2 border border-gray-300 rounded-lg text-sm mb-3 focus:ring-2 focus:ring-pink-500 outline-none" />
                                    <textarea placeholder="Details (Group rate code, shuttle info, etc.)" value={acc.details || ''} onChange={e => { const newList = [...section.content.accommodations]; newList[idx].details = e.target.value; handleFieldChange(section.id, 'accommodations', newList); }} className="w-full p-2 border border-gray-300 rounded-lg text-sm mb-3 resize-none h-20 focus:ring-2 focus:ring-pink-500 outline-none" />
                                    <input type="url" placeholder="Booking Link (URL)" value={acc.bookingUrl || ''} onChange={e => { const newList = [...section.content.accommodations]; newList[idx].bookingUrl = e.target.value; handleFieldChange(section.id, 'accommodations', newList); }} className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none" />
                                </div>
                            ))}
                            <button onClick={() => handleFieldChange(section.id, 'accommodations', [...(section.content.accommodations || []), { name: '', address: '', details: '', bookingUrl: '' }])} className="w-full py-3 bg-white border-2 border-dashed border-gray-300 rounded-xl font-bold text-gray-500 hover:text-pink-600 hover:border-pink-300 transition">
                                + Add Accommodation
                            </button>
                        </div>
                        {renderSectionStylesOverrides()}
                    </div>
                );
            case 'registry':
                return (
                    <div className="p-5 space-y-4 animate-fade-in">
                        {renderHeader('Gift Registry')}
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Section Heading</label><input type="text" value={section.content.heading || ''} onChange={e => handleFieldChange(section.id, 'heading', e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg mt-1 focus:ring-2 focus:ring-pink-500 outline-none" /></div>
                        
                        <div className="space-y-4 border-t border-gray-100 pt-4 mt-4">
                            <label className="text-sm font-bold text-gray-900 block">Native Cash Funds</label>
                            <p className="text-xs text-gray-500 mb-3">Create native cash funds (e.g. Honeymoon, House Fund) that guests can contribute to securely on your site.</p>
                            {(section.content.cashFunds || []).map((fund, idx) => (
                                <div key={idx} className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex flex-col gap-3 relative animate-fade-in">
                                    <button onClick={() => {
                                        const newFunds = [...section.content.cashFunds];
                                        newFunds.splice(idx, 1);
                                        handleFieldChange(section.id, 'cashFunds', newFunds);
                                    }} className="absolute top-2 right-2 text-gray-400 hover:text-red-500 p-1 font-bold z-10 text-lg leading-none">&times;</button>
                                    
                                    <div className="flex flex-col sm:flex-row gap-4 items-start pt-2">
                                        <div className="w-full sm:w-32 flex flex-col gap-2 flex-shrink-0">
                                            <div className="w-full h-32 bg-gray-200 rounded-xl overflow-hidden border border-gray-300 relative group shadow-inner">
                                                {fund.imageUrl ? (
                                                    <img src={fund.imageUrl} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                                                        <span className="text-3xl mb-1">💰</span>
                                                        <span className="text-[10px] font-bold uppercase tracking-wider">No Image</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="-mt-2">
                                                <ImageUpload 
                                                    weddingId={siteData.id} 
                                                    user={user} 
                                                    onUploadComplete={(url) => { const newFunds = [...section.content.cashFunds]; newFunds[idx].imageUrl = url; handleFieldChange(section.id, 'cashFunds', newFunds); }} 
                                                    buttonText="📷 Upload Photo"
                                                    hideLabel={true}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex-grow w-full space-y-3">
                                            <div>
                                                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Fund Name</label>
                                                <input type="text" placeholder="e.g. Honeymoon Fund" value={fund.title || ''} onChange={e => { const newFunds = [...section.content.cashFunds]; newFunds[idx].title = e.target.value; handleFieldChange(section.id, 'cashFunds', newFunds); }} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none font-bold text-gray-900 bg-white shadow-sm" />
                                            </div>
                                            
                                            <div className="flex flex-col sm:flex-row gap-3">
                                                <div className="flex-grow relative">
                                                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Or Paste Image URL</label>
                                                    <input type="text" placeholder="https://..." value={fund.imageUrl || ''} onChange={e => { const newFunds = [...section.content.cashFunds]; newFunds[idx].imageUrl = e.target.value; handleFieldChange(section.id, 'cashFunds', newFunds); }} className="w-full p-2.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-pink-500 outline-none bg-gray-50" />
                                                </div>
                                                <div className="relative w-full sm:w-32 flex-shrink-0">
                                                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Goal Amount</label>
                                                    <div className="relative shadow-sm rounded-lg overflow-hidden">
                                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-bold">$</span>
                                                        <input type="number" placeholder="Optional" value={fund.goalAmount || ''} onChange={e => { const newFunds = [...section.content.cashFunds]; newFunds[idx].goalAmount = e.target.value; handleFieldChange(section.id, 'cashFunds', newFunds); }} className="w-full pl-7 p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none bg-white" />
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div>
                                                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Description</label>
                                                <textarea placeholder="e.g. Help us travel to Bali!" value={fund.description || ''} onChange={e => { const newFunds = [...section.content.cashFunds]; newFunds[idx].description = e.target.value; handleFieldChange(section.id, 'cashFunds', newFunds); }} className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none h-20 resize-none bg-white shadow-sm" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <button onClick={() => handleFieldChange(section.id, 'cashFunds', [...(section.content.cashFunds || []), { id: Date.now().toString(), title: '', description: '', goalAmount: '', currentAmount: 0, imageUrl: '' }])} className="w-full py-3 bg-white border-2 border-dashed border-gray-300 rounded-xl font-bold text-gray-500 hover:text-pink-600 hover:border-pink-300 transition shadow-sm">
                                + Add Cash Fund
                            </button>
                        </div>

                        <div className="space-y-4 border-t border-gray-100 pt-4 mt-4">
                            <label className="text-sm font-bold text-gray-900 block">Universal Registry (Physical Gifts)</label>
                            <p className="text-xs text-gray-500 mb-3">Paste a link from any store (Target, Amazon, Etsy). We'll automatically grab the details!</p>
                            
                            <div className="flex gap-2 mb-4">
                                <input type="text" id={`url-input-${section.id}`} placeholder="Paste product URL here..." className="flex-grow p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none shadow-sm" />
                                <button 
                                    id={`url-btn-${section.id}`}
                                    onClick={async () => {
                                        const input = document.getElementById(`url-input-${section.id}`);
                                        const btn = document.getElementById(`url-btn-${section.id}`);
                                        if (!input || !input.value) return;
                                        
                                        let url = input.value;
                                        if (!url.startsWith('http')) url = 'https://' + url;
                                        
                                        const originalBtnText = btn.innerText;
                                        btn.innerText = 'Fetching...';
                                        input.disabled = true;
                                        btn.disabled = true;
                                        
                                        try {
                                            // Using Microlink API to scrape OpenGraph data (Title, Image, Publisher)
                                            const res = await fetch(`https://api.microlink.io/?url=${encodeURIComponent(url)}`);
                                            const data = await res.json();
                                            
                                            const newGift = {
                                                id: Date.now().toString(),
                                                url: url,
                                                title: data.data?.title || '',
                                                imageUrl: data.data?.image?.url || data.data?.logo?.url || '',
                                                storeName: data.data?.publisher || new URL(url).hostname.replace('www.', ''),
                                                price: '' // Price is hard to scrape reliably, left blank for user input
                                            };
                                            
                                            handleFieldChange(section.id, 'physicalGifts', [...(section.content.physicalGifts || []), newGift]);
                                        } catch (err) {
                                            // Fallback to empty inputs if API fails or blocks scraping
                                            handleFieldChange(section.id, 'physicalGifts', [...(section.content.physicalGifts || []), { id: Date.now().toString(), url, title: '', imageUrl: '', storeName: '', price: '' }]);
                                        } finally {
                                            input.value = '';
                                            input.disabled = false;
                                            btn.disabled = false;
                                            btn.innerText = originalBtnText;
                                        }
                                    }}
                                    className="px-5 py-2.5 bg-gray-900 text-white font-bold rounded-lg text-sm hover:bg-gray-800 transition whitespace-nowrap disabled:opacity-50 shadow-sm"
                                >
                                    Add Gift
                                </button>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {(section.content.physicalGifts || []).map((gift, idx) => (
                                    <div key={idx} className="bg-gray-50 p-3 rounded-xl border border-gray-200 relative animate-fade-in flex flex-col gap-2 shadow-sm group">
                                        <button onClick={() => {
                                            const newGifts = [...section.content.physicalGifts];
                                            newGifts.splice(idx, 1);
                                            handleFieldChange(section.id, 'physicalGifts', newGifts);
                                        }} className="absolute -top-2 -right-2 bg-white rounded-full border border-gray-200 text-gray-400 hover:text-red-500 w-6 h-6 flex items-center justify-center font-bold z-10 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">&times;</button>
                                        
                                        <div className="flex gap-3">
                                            <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0 border border-gray-300 relative">
                                                {gift.imageUrl ? (
                                                    <img src={gift.imageUrl} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex flex-col items-center justify-center text-[9px] text-gray-400 uppercase font-bold tracking-wider text-center p-1">
                                                        <span>🎁</span>
                                                        <span>No Img</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-grow flex flex-col justify-center space-y-1.5 w-full overflow-hidden">
                                                <input type="text" placeholder="Gift Title" value={gift.title || ''} onChange={e => { const newGifts = [...section.content.physicalGifts]; newGifts[idx].title = e.target.value; handleFieldChange(section.id, 'physicalGifts', newGifts); }} className="w-full p-1.5 border border-gray-300 rounded text-xs font-bold text-gray-900 bg-white outline-none focus:ring-1 focus:ring-pink-500 truncate" />
                                                <div className="flex gap-1.5">
                                                    <input type="text" placeholder="Store" value={gift.storeName || ''} onChange={e => { const newGifts = [...section.content.physicalGifts]; newGifts[idx].storeName = e.target.value; handleFieldChange(section.id, 'physicalGifts', newGifts); }} className="w-[60%] p-1.5 border border-gray-300 rounded text-[10px] bg-white text-gray-600 outline-none focus:ring-1 focus:ring-pink-500 truncate" />
                                                    <input type="text" placeholder="Price $" value={gift.price || ''} onChange={e => { const newGifts = [...section.content.physicalGifts]; newGifts[idx].price = e.target.value; handleFieldChange(section.id, 'physicalGifts', newGifts); }} className="w-[40%] p-1.5 border border-gray-300 rounded text-[10px] bg-white text-gray-600 outline-none focus:ring-1 focus:ring-pink-500" />
                                                </div>
                                            </div>
                                        </div>
                                        <a href={gift.url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-500 hover:underline truncate px-1">
                                            {gift.url}
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-4 border-t border-gray-100 pt-4 mt-4">
                            <label className="text-sm font-bold text-gray-900 block">External Registry Links</label>
                            {(section.content.links || []).map((link, idx) => (
                                <div key={idx} className="bg-gray-50 p-3 rounded-xl border border-gray-200 flex flex-col gap-2 relative pr-8">
                                    <button onClick={() => {
                                        const newList = [...section.content.links];
                                        newList.splice(idx, 1);
                                        handleFieldChange(section.id, 'links', newList);
                                    }} className="absolute top-1/2 -translate-y-1/2 right-2 text-gray-400 hover:text-red-500 p-1 font-bold">✕</button>
                                    <input type="text" placeholder="Store Name (e.g. Target, Zola)" value={link.storeName || ''} onChange={e => { const newList = [...section.content.links]; newList[idx].storeName = e.target.value; handleFieldChange(section.id, 'links', newList); }} className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none" />
                                    <input type="url" placeholder="Registry URL" value={link.url || ''} onChange={e => { const newList = [...section.content.links]; newList[idx].url = e.target.value; handleFieldChange(section.id, 'links', newList); }} className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none" />
                                </div>
                            ))}
                            <button onClick={() => handleFieldChange(section.id, 'links', [...(section.content.links || []), { storeName: '', url: '' }])} className="w-full py-3 bg-white border-2 border-dashed border-gray-300 rounded-xl font-bold text-gray-500 hover:text-pink-600 hover:border-pink-300 transition">
                                + Add Registry
                            </button>
                        </div>
                        {renderSectionStylesOverrides()}
                    </div>
                );
            case 'gallery':
                return (
                    <div className="p-5 space-y-4 animate-fade-in">
                        {renderHeader('Photo Gallery')}
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Section Heading</label><input type="text" value={section.content.heading || ''} onChange={e => handleFieldChange(section.id, 'heading', e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg mt-1 focus:ring-2 focus:ring-pink-500 outline-none" /></div>
                        <div className="border-t border-gray-100 pt-4 space-y-3">
                            <label className="text-sm font-bold text-gray-900 block">Images</label>
                            {(section.content.images || []).map((img, idx) => (
                                <div key={idx} className="flex gap-3 items-center bg-gray-50 p-2 rounded-xl border border-gray-200">
                                    <img src={img.url || 'https://placehold.co/40'} className="w-12 h-12 object-cover rounded-lg bg-gray-200 shadow-sm border border-gray-300" alt="thumb"/>
                                    <input type="text" value={img.url || ''} onChange={e => { const newImgs = [...section.content.images]; newImgs[idx].url = e.target.value; handleFieldChange(section.id, 'images', newImgs); }} placeholder="Paste Image URL" className="flex-1 p-2 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-pink-500 outline-none" />
                                    <button onClick={() => { const newImgs = [...section.content.images]; newImgs.splice(idx, 1); handleFieldChange(section.id, 'images', newImgs); }} className="p-2 text-gray-400 hover:text-red-500 bg-white rounded-lg shadow-sm border border-gray-100 font-bold">✕</button>
                                </div>
                            ))}
                            <button onClick={() => handleFieldChange(section.id, 'images', [...(section.content.images || []), { url: '' }])} className="w-full py-3 bg-white border-2 border-dashed border-gray-300 rounded-xl font-bold text-gray-500 hover:text-pink-600 hover:border-pink-300 transition">
                                + Add Image URL
                            </button>
                            <ImageUpload 
                                weddingId={siteData.id} 
                                user={user} 
                                onUploadComplete={(url) => handleFieldChange(section.id, 'images', [...(section.content.images || []), { url }])} 
                                buttonText="+ Upload & Crop Image"
                                hideLabel={true}
                            />
                        </div>
                        {renderSectionStylesOverrides()}
                    </div>
                );
            case 'rsvp':
                return (
                    <div className="p-5 space-y-4 animate-fade-in">
                        {renderHeader('RSVP Section')}
                        <div><label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Section Heading</label><input type="text" value={section.content.heading || ''} onChange={e => handleFieldChange(section.id, 'heading', e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg mt-1 focus:ring-2 focus:ring-pink-500 outline-none" /></div>
                        
                        <div className="grid grid-cols-2 gap-4 mt-4 border-t border-gray-100 pt-4">
                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">RSVP Deadline</label>
                                <input type="date" value={section.content.deadline || ''} onChange={e => handleFieldChange(section.id, 'deadline', e.target.value)} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none bg-white text-sm" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Max Guests per RSVP</label>
                                <input type="number" min="1" max="20" placeholder="No limit" value={section.content.maxPartySize || ''} onChange={e => handleFieldChange(section.id, 'maxPartySize', parseInt(e.target.value))} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none bg-white text-sm" />
                            </div>
                        </div>

                        <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 mt-2">
                            <p className="text-sm text-blue-800 font-medium">The RSVP form is automatically connected to your dashboard.</p>
                        </div>
                        {renderSectionStylesOverrides()}
                    </div>
                );
            default:
                return null;
        }
    };

    const renderSectionPreview = (section, index) => {
        if (!section || !section.enabled) return null;
        const props = {
            content: section.content,
            theme: siteData.theme,
            isEditing: true,
            onFieldChange: (field, value) => handleFieldChange(section.id, field, value),
            weddingId: siteData.id
        };

        const animationClass = siteData.theme?.enableAnimations ? 'wedding-animate-fade-in' : '';
        const animationStyle = siteData.theme?.enableAnimations ? { animationFillMode: 'both', animationDuration: '0.8s', animationDelay: `${index * 150}ms` } : {};

        let SectionComponent = null;
        switch (section.type) {
            case 'hero': SectionComponent = <HeroSection {...props} />; break;
            case 'story': SectionComponent = <StorySection {...props} />; break;
            case 'events': SectionComponent = <EventsSection {...props} />; break;
            case 'travel': SectionComponent = <TravelSection {...props} />; break;
            case 'rsvp': SectionComponent = <RsvpSection {...props} />; break;
            case 'gallery': SectionComponent = <GallerySection {...props} />; break;
            case 'registry': SectionComponent = <RegistrySection {...props} />; break;
            default: SectionComponent = <div className="p-8 text-center text-red-500">Error: Unknown section type "{section.type}".</div>; break;
        }

        return (
            <div className={animationClass} style={animationStyle}>
                {SectionComponent}
            </div>
        );
    };

    if (loading) return <div className="flex h-screen items-center justify-center bg-gray-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div></div>;
    if (error) return <div className="text-center p-10 text-red-500 font-bold bg-red-50 m-10 rounded-xl">{error}</div>;

    return (
        <div className="flex flex-col h-screen relative bg-gray-50 font-sans">
            {croppingHeroImage && (
                <div className="relative z-[1000]">
                    <ImageCropperModal
                        imageUrl={croppingHeroImage.url}
                        aspectRatio={croppingHeroImage.aspectRatio}
                        onClose={() => setCroppingHeroImage(null)}
                        onCrop={handleHeroCropComplete}
                    />
                </div>
            )}
            {/* UNIFIED HUB TOP NAVIGATION */}
            <div className="bg-white shadow-[0_2px_10px_rgba(0,0,0,0.05)] border-b border-gray-200 z-[60] flex-shrink-0 px-4 py-3 flex flex-col xl:flex-row items-start xl:items-center justify-between gap-3 md:gap-4">
                <div className="flex items-center gap-3 w-full xl:w-auto">
                    <button onClick={() => navigateTo('dashboard')} className="text-gray-500 hover:text-pink-600 font-bold flex items-center gap-1.5 text-sm bg-gray-100 px-3 py-1.5 rounded-lg transition border border-gray-200">
                        <span className="text-lg leading-none">&larr;</span> <span className="hidden sm:inline">Back</span>
                    </button>
                    <div className="h-6 w-px bg-gray-300 hidden sm:block"></div>
                    <input
                        type="text"
                        value={siteData.title || ''}
                        onChange={(e) => setSiteData(prev => ({ ...prev, title: e.target.value }))}
                        className="font-serif font-bold text-gray-900 text-lg md:text-xl tracking-tight bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-pink-500 outline-none px-1 py-0.5 transition-colors flex-1 xl:w-80 truncate"
                        placeholder="Wedding Website Name"
                        title="Click to edit website name"
                    />
                </div>
                
                {/* Pill-based Grouped Tabs (Pro Style) */}
                <div className="overflow-x-auto custom-scrollbar no-scrollbar-mobile w-full xl:w-auto pb-2 xl:pb-0 -mb-2 xl:mb-0">
                    <div className="flex gap-6 items-end min-w-max">
                        <div className="flex flex-col gap-1.5 flex-shrink-0">
                            <div className="flex bg-white rounded-xl shadow-sm border border-gray-200 p-1 gap-1">
                                {[
                                    { id: 'website', icon: PlannerIcons.Palette, label: 'Website Builder' },
                                    { id: 'guests', icon: PlannerIcons.Users, label: 'Guest List & RSVPs' },
                                    { id: 'checklist', icon: PlannerIcons.Check, label: 'Checklist' },
                                    { id: 'budget', icon: PlannerIcons.Dollar, label: 'Budget' },
                                    { id: 'rentals', icon: PlannerIcons.Box, label: 'Rentals' }
                                ].map(tab => {
                                    const IconComponent = tab.icon;
                                    return (
                                        <button 
                                            key={tab.id}
                                            onClick={() => setHubTab(tab.id)}
                                            className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 font-bold text-[10px] sm:text-sm rounded-lg transition-all whitespace-nowrap flex-shrink-0 ${hubTab === tab.id ? 'bg-pink-50 text-pink-700 shadow-sm ring-1 ring-pink-200' : 'bg-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
                                        >
                                            <span className={`${hubTab === tab.id ? 'text-pink-600' : 'text-gray-400'}`}>
                                                <IconComponent />
                                            </span> 
                                            <span>{tab.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* TAB CONTENTS */}
            <div className="flex-1 overflow-hidden relative">
                
                {/* 1. WEBSITE BUILDER TAB */}
                {hubTab === 'website' && (
                    <div className="flex flex-col h-full relative animate-fade-in bg-white">
                        
                        {/* NEW: FULL SCREEN PREVIEW MODAL */}
                        {fullScreenPreview && (
                            <div className="fixed inset-0 z-[500] bg-gray-200 flex flex-col h-screen w-screen overflow-hidden animate-fade-in">
                                {/* Viewport Toggles */}
                                <div className="bg-gray-100 border-b border-gray-300 p-2.5 flex justify-between items-center shadow-inner z-10 flex-shrink-0 px-4 md:px-8">
                                    <div className="flex-1"></div>
                                    <div className="bg-white p-1 rounded-lg flex border border-gray-200 shadow-sm mx-auto">
                                        <button 
                                            onClick={() => setPreviewDevice('desktop')} 
                                            className={`px-3 py-1.5 md:px-4 rounded-md text-sm font-bold flex items-center gap-2 transition ${previewDevice === 'desktop' ? 'bg-gray-900 text-white shadow' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`}
                                        >
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                            <span className="hidden sm:inline">Desktop</span>
                                        </button>
                                        <button 
                                            onClick={() => setPreviewDevice('mobile')} 
                                            className={`px-3 py-1.5 md:px-4 rounded-md text-sm font-bold flex items-center gap-2 transition ${previewDevice === 'mobile' ? 'bg-gray-900 text-white shadow' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`}
                                        >
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                                            <span className="hidden sm:inline">Mobile</span>
                                        </button>
                                    </div>
                                    <div className="flex-1 flex justify-end">
                                        <button onClick={() => setFullScreenPreview(false)} className="bg-white text-gray-800 px-4 py-2 rounded-full shadow-sm border border-gray-200 font-bold hover:bg-gray-50 flex items-center gap-2 text-sm transition">
                                            ✕ <span className="hidden sm:inline">Close</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Preview Area */}
                                <div className="flex-1 overflow-y-auto custom-scrollbar p-4 md:p-8 flex justify-center items-start">
                                    <div className={`bg-white shadow-2xl relative transition-all duration-500 flex flex-col flex-shrink-0 ${previewDevice === 'mobile' ? 'w-[375px] rounded-[2rem] border-[12px] border-gray-900 overflow-hidden h-[812px] mt-0 md:mt-4 mb-12' : 'w-full max-w-[1400px] min-h-full'}`}>
                                        {previewDevice === 'mobile' && (
                                            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-gray-900 rounded-b-xl z-[60]"></div>
                                        )}
                                        
                                        {previewDevice === 'mobile' ? (
                                            <IFramePreview 
                                                className="w-full h-full border-none bg-transparent rounded-[1rem] z-10"
                                            >
                                                <div className="min-h-full" style={{ fontFamily: siteData.theme?.fontFamily, backgroundColor: siteData.theme?.backgroundColor }}>
                                                    {siteData.sections.map(section => {
                                                        if (!section.enabled) return null;
                                                        const props = {
                                                            content: section.content,
                                                            theme: siteData.theme,
                                                            isEditing: false,
                                                            weddingId: siteData.id
                                                        };
                                                        switch (section.type) {
                                                            case 'hero': return <HeroSection key={section.id} {...props} />;
                                                            case 'story': return <StorySection key={section.id} {...props} />;
                                                            case 'events': return <EventsSection key={section.id} {...props} />;
                                                            case 'travel': return <TravelSection key={section.id} {...props} />;
                                                            case 'rsvp': return <RsvpSection key={section.id} {...props} />;
                                                            case 'gallery': return <GallerySection key={section.id} {...props} />;
                                                            case 'registry': return <RegistrySection key={section.id} {...props} />;
                                                            default: return null;
                                                        }
                                                    })}
                                                </div>
                                            </IFramePreview>
                                        ) : (
                                            <div className="flex-1 overflow-y-auto custom-scrollbar relative" style={{ fontFamily: siteData.theme?.fontFamily, backgroundColor: siteData.theme?.backgroundColor }}>
                                                {siteData.sections.map(section => {
                                                    if (!section.enabled) return null;
                                                    const props = {
                                                        content: section.content,
                                                        theme: siteData.theme,
                                                        isEditing: false,
                                                        weddingId: siteData.id
                                                    };
                                                    switch (section.type) {
                                                        case 'hero': return <HeroSection key={section.id} {...props} />;
                                                        case 'story': return <StorySection key={section.id} {...props} />;
                                                        case 'events': return <EventsSection key={section.id} {...props} />;
                                                        case 'travel': return <TravelSection key={section.id} {...props} />;
                                                        case 'rsvp': return <RsvpSection key={section.id} {...props} />;
                                                        case 'gallery': return <GallerySection key={section.id} {...props} />;
                                                        case 'registry': return <RegistrySection key={section.id} {...props} />;
                                                        default: return null;
                                                    }
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Floating Mobile View Toggle */}
                        <div className="md:hidden fixed bottom-6 left-1/2 transform -translate-x-1/2 z-[150] bg-gray-900 p-1 rounded-full flex shadow-2xl border border-gray-700 w-auto">
                            <button onClick={() => setMobileView('editor')} className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${mobileView === 'editor' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-300'}`}>
                                Layout
                            </button>
                            <button onClick={() => setMobileView('preview')} className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${mobileView === 'preview' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-300'}`}>
                                Website
                            </button>
                        </div>

                        {templateToApply && (
                            <div className="fixed inset-0 bg-black bg-opacity-60 z-[300] flex items-center justify-center p-4 animate-fade-in">
                                <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-sm">
                                    <h3 className="text-xl font-bold mb-4 text-gray-900">Apply Template?</h3>
                                    <p className="mb-6 text-gray-600 text-sm">Applying the <strong>{templateToApply.name}</strong> template will replace your current design and sections. Are you sure you want to continue?</p>
                                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                        <button onClick={() => setTemplateToApply(null)} className="px-5 py-2.5 text-gray-600 font-semibold hover:bg-gray-100 rounded-lg transition">Cancel</button>
                                        <button onClick={confirmApplyTemplate} className="px-5 py-2.5 bg-pink-600 text-white rounded-lg font-bold hover:bg-pink-700 transition shadow-md">Apply Template</button>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Builder Top Bar (Save/Publish only) */}
                        <header className="bg-gray-50 border-b border-gray-200 p-2 md:p-3 flex justify-between items-center flex-shrink-0 z-20 transition-all">
                            <div className="flex items-center gap-2">
                                {/* NEW: Sidebar Toggle Button */}
                                <button 
                                    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} 
                                    className="hidden md:flex p-1.5 bg-white border border-gray-200 rounded-md hover:bg-gray-100 text-gray-600 transition shadow-sm" 
                                    title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        {isSidebarCollapsed ? (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                                        ) : (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                                        )}
                                    </svg>
                                </button>

                                {/* NEW: Undo / Redo Buttons */}
                                <div className="flex gap-1 ml-1 bg-white p-0.5 rounded-lg border border-gray-200 shadow-sm hidden sm:flex">
                                    <button onClick={handleUndo} disabled={past.length === 0} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 disabled:opacity-30 transition" title="Undo">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                                    </button>
                                    <div className="w-px bg-gray-200 mx-0.5 my-1"></div>
                                    <button onClick={handleRedo} disabled={future.length === 0} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600 disabled:opacity-30 transition" title="Redo">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" /></svg>
                                    </button>
                                </div>

                                <span className="bg-blue-100 text-blue-800 text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-wider hidden lg:block">Editor Mode</span>
                                {isAutoSaving && <span className="text-xs font-semibold text-gray-500 animate-pulse hidden md:block ml-2">Auto-saving...</span>}
                            </div>
                            <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0 w-full sm:w-auto justify-end">
                                <button onClick={() => setFullScreenPreview(true)} className="text-xs font-bold text-gray-600 hover:text-gray-900 bg-white border border-gray-300 px-3 py-1.5 rounded-lg transition shadow-sm flex items-center gap-1.5">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                    <span className="hidden sm:inline">Preview</span>
                                </button>
                                <a href={`/w/${siteData.subdomain}`} target="_blank" rel="noopener noreferrer" className={`text-xs font-bold text-gray-600 hover:text-gray-900 bg-white border border-gray-300 px-3 py-1.5 rounded-lg transition hidden md:flex items-center gap-1.5 ${!siteData.subdomain ? 'pointer-events-none opacity-50' : ''}`}>
                                    Live URL
                                </a>
                                <button onClick={handlePublish} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition shadow-sm border ${siteData.isPublished ? 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100' : 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'}`}>
                                    {siteData.isPublished ? 'Unpublish Site' : 'Publish Live'}
                                </button>
                                <button onClick={handleSave} disabled={saving || isAutoSaving} className="bg-gray-900 text-white px-5 py-1.5 rounded-lg disabled:bg-gray-400 text-xs font-bold flex items-center gap-2 shadow-sm hover:bg-black transition">
                                    {(saving || isAutoSaving) ? <span className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span> : '💾'} 
                                    <span>{(saving || isAutoSaving) ? 'Saving...' : 'Save'}</span>
                                </button>
                            </div>
                        </header>

                        <div className="flex flex-grow overflow-hidden relative">
                            {/* LEFT SIDEBAR - WIZARD */}
                            <aside className={`bg-white border-gray-200 flex flex-col flex-shrink-0 z-10 pb-24 md:pb-0 transition-all duration-300 ease-in-out overflow-hidden ${isSidebarCollapsed ? 'md:w-0 md:border-r-0' : 'w-full md:w-[320px] lg:w-[380px] md:border-r'} ${mobileView === 'preview' ? 'hidden md:flex' : 'flex'}`}>
                                <div className="w-full md:w-[320px] lg:w-[380px] flex flex-col h-full flex-shrink-0 overflow-hidden bg-gray-50">
                                    
                                    {/* Wizard Header & Progress */}
                                    <div className="p-5 border-b border-gray-200 bg-white flex-shrink-0">
                                        <div className="flex items-center justify-between mb-3 px-2 relative before:absolute before:top-1/2 before:left-2 before:right-2 before:h-1 before:bg-gray-100 before:-z-10">
                                            <div className="absolute top-1/2 left-2 h-1 bg-pink-500 -z-10 transition-all duration-500" style={{ width: editorStep === 1 ? '0%' : editorStep === 2 ? '50%' : 'calc(100% - 1rem)' }}></div>
                                            <button onClick={() => setEditorStep(1)} className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs transition-colors border-4 border-white shadow-sm ${editorStep >= 1 ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`} title="Setup">1</button>
                                            <button onClick={() => setEditorStep(2)} className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs transition-colors border-4 border-white shadow-sm ${editorStep >= 2 ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`} title="Design">2</button>
                                            <button onClick={() => setEditorStep(3)} className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs transition-colors border-4 border-white shadow-sm ${editorStep >= 3 ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`} title="Content">3</button>
                                        </div>
                                        <div className="text-center">
                                            <h2 className="text-sm font-bold text-gray-900">
                                                {editorStep === 1 ? 'Site Setup & Privacy' : editorStep === 2 ? 'Theme & Branding' : 'Page Layout & Blocks'}
                                            </h2>
                                        </div>
                                    </div>

                                    {/* Wizard Scrollable Content */}
                                    <div className="flex-1 overflow-y-auto custom-scrollbar p-5 space-y-6">
                                        
                                        {/* STEP 1: SETUP */}
                                        {editorStep === 1 && (
                                            <div className="space-y-6 animate-fade-in">
                                                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                                                    <label className="font-bold text-xs text-gray-500 uppercase tracking-widest mb-2 block">Custom URL Path</label>
                                                    <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg focus-within:ring-2 focus-within:ring-pink-500 transition px-3 py-2">
                                                        <span className="text-gray-400 text-sm font-mono">/w/</span>
                                                        <input
                                                            type="text"
                                                            value={siteData.subdomain || ''}
                                                            onChange={(e) => setSiteData(prev => ({ ...prev, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                                                            className="font-mono text-sm bg-transparent border-none outline-none w-full text-gray-900 font-bold ml-1"
                                                            placeholder="your-unique-url"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-4">
                                                    <h4 className="font-bold text-sm text-gray-900 border-b border-gray-100 pb-2">Privacy & Security</h4>
                                                    <label className="flex items-start gap-3 cursor-pointer group">
                                                        <input type="checkbox" checked={siteData.isPasswordProtected || false} onChange={e => { saveHistory(); setSiteData(prev => ({ ...prev, isPasswordProtected: e.target.checked })) }} className="mt-0.5 w-4 h-4 text-pink-600 rounded focus:ring-pink-500 border-gray-300" />
                                                        <div>
                                                            <span className="block font-bold text-gray-800 text-sm group-hover:text-pink-600 transition">Password Protect Website</span>
                                                            <span className="block text-xs text-gray-500 mt-1">Require guests to enter a code to view your site.</span>
                                                        </div>
                                                    </label>
                                                    {siteData.isPasswordProtected && (
                                                        <div className="mt-3 ml-7 animate-fade-in">
                                                            <input type="text" value={siteData.password || ''} onChange={e => { saveHistory(); setSiteData(prev => ({ ...prev, password: e.target.value })) }} placeholder="Access Code (e.g. FOREVER2026)" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none text-sm font-medium bg-gray-50" />
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-4">
                                                    <h4 className="font-bold text-sm text-gray-900 border-b border-gray-100 pb-2">SEO & Social Sharing</h4>
                                                    <div>
                                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Site Title</label>
                                                        <input type="text" value={siteData.seoTitle || ''} onChange={e => { saveHistory(); setSiteData(prev => ({ ...prev, seoTitle: e.target.value })) }} placeholder="Site Title (e.g. Sarah & James's Wedding)" className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none text-sm bg-gray-50" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Description</label>
                                                        <textarea value={siteData.seoDescription || ''} onChange={e => { saveHistory(); setSiteData(prev => ({ ...prev, seoDescription: e.target.value })) }} placeholder="Join us as we celebrate..." className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none text-sm h-20 resize-none bg-gray-50" />
                                                    </div>
                                                </div>

                                                <div className="bg-purple-50 p-5 rounded-xl border border-purple-100 shadow-sm">
                                                    <h4 className="font-bold text-sm text-purple-900 border-b border-purple-200 pb-2 mb-4">Guest Tools</h4>
                                                    <label className="flex items-start gap-3 cursor-pointer group bg-white p-3 rounded-lg border border-purple-100 hover:border-purple-300 transition shadow-sm">
                                                        <input type="checkbox" checked={siteData.enableSaveTheDate || false} onChange={(e) => { saveHistory(); setSiteData(prev => ({ ...prev, enableSaveTheDate: e.target.checked })); }} className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500 cursor-pointer" />
                                                        <div>
                                                            <span className="text-sm font-bold text-gray-900 block">Collect Mailing Addresses</span>
                                                            <span className="text-[10px] text-gray-500">Adds a pop-up to gather guest info.</span>
                                                        </div>
                                                    </label>
                                                    {siteData.enableSaveTheDate && (
                                                        <div className="pl-6 space-y-3 animate-fade-in border-l-2 border-purple-200 ml-2 mt-4">
                                                            <label className="flex items-center gap-2 cursor-pointer">
                                                                <input type="checkbox" checked={siteData.allowAdditionalGuests || false} onChange={(e) => { saveHistory(); setSiteData(prev => ({ ...prev, allowAdditionalGuests: e.target.checked })); }} className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500 cursor-pointer" />
                                                                <span className="text-sm font-medium text-gray-700">Allow guests to declare "+X" additional people</span>
                                                            </label>
                                                            {siteData.allowAdditionalGuests && (
                                                                <div className="flex items-center gap-3 bg-white p-2 rounded-lg border border-purple-100 w-fit mt-2 shadow-sm">
                                                                    <span className="text-xs font-bold text-gray-600">Max Allowed:</span>
                                                                    <input type="number" min="1" value={siteData.maxAdditionalGuests || 1} onChange={(e) => { saveHistory(); setSiteData(prev => ({ ...prev, maxAdditionalGuests: parseInt(e.target.value) || 1 })); }} className="w-16 p-1.5 border border-purple-200 rounded-md text-sm font-bold text-center outline-none focus:ring-1 focus:ring-purple-500" />
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* STEP 2: THEME */}
                                        {editorStep === 2 && (
                                            <div className="space-y-6 animate-fade-in">
                                                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                                                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block mb-3">Designer Palettes</label>
                                                    <div className="flex flex-wrap gap-3 mb-6">
                                                        {colorPalettes.map(palette => {
                                                            const isActive = siteData.theme.primaryColor === palette.primary && siteData.theme.backgroundColor === palette.background;
                                                            return (
                                                                <button
                                                                    key={palette.name}
                                                                    onClick={() => {
                                                                        saveHistory();
                                                                        setSiteData(prev => ({ ...prev, theme: { ...prev.theme, primaryColor: palette.primary, backgroundColor: palette.background } }));
                                                                    }}
                                                                    className={`flex items-center gap-2 px-3 py-2 rounded-full border transition-all duration-300 ${isActive ? 'border-gray-900 bg-gray-900 shadow-md transform scale-[1.02]' : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:shadow-sm hover:bg-white'}`}
                                                                    title={palette.name}
                                                                >
                                                                    <div className="flex -space-x-1.5 drop-shadow-sm">
                                                                        <div className="w-5 h-5 rounded-full border border-white/50 z-10" style={{ backgroundColor: palette.primary }}></div>
                                                                        <div className={`w-5 h-5 rounded-full border z-0 ${isActive ? 'border-white/20' : 'border-black/10'}`} style={{ backgroundColor: palette.background }}></div>
                                                                    </div>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>

                                                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest block mb-2 border-t border-gray-100 pt-5">Typography</label>
                                                    <select value={siteData.theme.fontFamily} onChange={(e) => { saveHistory(); setSiteData(prev => ({ ...prev, theme: { ...prev.theme, fontFamily: e.target.value } })); }} className="w-full p-3 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-pink-500 outline-none transition font-medium">
                                                        {googleFonts.map(font => <option key={font} value={font}>{font}</option>)}
                                                    </select>
                                                    
                                                    <div className="border-t border-gray-100 mt-6 pt-5">
                                                        <label className="flex items-center gap-3 cursor-pointer group">
                                                            <input type="checkbox" checked={siteData.theme.enableAnimations || false} onChange={(e) => { saveHistory(); setSiteData(prev => ({ ...prev, theme: { ...prev.theme, enableAnimations: e.target.checked } })); }} className="w-5 h-5 text-pink-600 rounded focus:ring-pink-500 cursor-pointer border-gray-300" />
                                                            <div>
                                                                <span className="text-sm font-bold text-gray-800 block group-hover:text-pink-600 transition">Enable Scroll Animations</span>
                                                                <span className="text-[10px] text-gray-500 block mt-0.5">Adds smooth fade-in effects as guests scroll.</span>
                                                            </div>
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* STEP 3: CONTENT */}
                                        {editorStep === 3 && (
                                            <div className="space-y-4 animate-fade-in">
                                                <div className="flex justify-between items-center mb-1">
                                                    <h3 className="font-bold text-gray-900 text-lg">Page Layout</h3>
                                                    <div className="relative">
                                                        <button onClick={() => setShowAddSection(!showAddSection)} className="text-xs bg-gray-900 text-white px-3 py-2 rounded-lg font-bold hover:bg-black transition shadow-sm flex items-center gap-1">
                                                            <span className="text-lg leading-none">+</span> Add Block
                                                        </button>
                                                        {showAddSection && (
                                                            <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden z-30 animate-fade-in divide-y divide-gray-50">
                                                                <button onClick={() => addSection('hero')} className="block w-full text-left px-4 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50 hover:text-pink-600 transition">🌟 Hero / Cover</button>
                                                                <button onClick={() => addSection('story')} className="block w-full text-left px-4 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50 hover:text-pink-600 transition">📖 Our Story</button>
                                                                <button onClick={() => addSection('events')} className="block w-full text-left px-4 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50 hover:text-pink-600 transition">📅 Schedule</button>
                                                                <button onClick={() => addSection('travel')} className="block w-full text-left px-4 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50 hover:text-pink-600 transition">✈️ Travel</button>
                                                                <button onClick={() => addSection('gallery')} className="block w-full text-left px-4 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50 hover:text-pink-600 transition">🖼️ Gallery</button>
                                                                <button onClick={() => addSection('registry')} className="block w-full text-left px-4 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50 hover:text-pink-600 transition">🎁 Registry</button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <p className="text-xs text-gray-500 mb-3">Drag to reorder. Click to edit.</p>
                                                
                                                <div className="space-y-3">
                                                    {siteData.sections.map((section, index) => (
                                                        <div
                                                            key={section.id}
                                                            draggable
                                                            onDragStart={() => (dragItem.current = index)}
                                                            onDragEnter={() => (dragOverItem.current = index)}
                                                            onDragEnd={handleDrop}
                                                            onDragOver={(e) => e.preventDefault()}
                                                            onClick={() => { setActiveSectionId(section.id); setMobileView('preview'); }}
                                                            className={`p-4 rounded-xl cursor-grab active:cursor-grabbing border flex flex-col transition-all duration-200 ${activeSectionId === section.id ? 'bg-white border-pink-400 shadow-md ring-2 ring-pink-500/10' : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'}`}
                                                        >
                                                            <div className="flex justify-between items-center">
                                                                <div className="flex items-center gap-3 min-w-0">
                                                                    <div className="text-gray-300 opacity-50 hover:opacity-100 hidden sm:block font-bold">⋮⋮</div>
                                                                    <span className={`font-bold text-sm truncate ${!section.enabled ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{section.content.heading || section.content.title || section.type}</span>
                                                                </div>
                                                                <div className="flex items-center gap-1 bg-gray-50 p-1 rounded-lg border border-gray-200" onClick={e => e.stopPropagation()}>
                                                                    <button onClick={() => moveSection(index, 'up')} disabled={index===0} className="w-6 h-6 flex items-center justify-center hover:bg-white rounded-md text-gray-600 disabled:opacity-30 font-bold transition">↑</button>
                                                                    <button onClick={() => moveSection(index, 'down')} disabled={index===siteData.sections.length-1} className="w-6 h-6 flex items-center justify-center hover:bg-white rounded-md text-gray-600 disabled:opacity-30 font-bold transition">↓</button>
                                                                </div>
                                                            </div>
                                                            
                                                            {activeSectionId === section.id && (
                                                                <div className="flex justify-between items-center pt-3 border-t border-pink-100 mt-2 animate-fade-in" onClick={e => e.stopPropagation()}>
                                                                    <label className="flex items-center text-xs gap-1.5 cursor-pointer text-gray-600 font-medium hover:text-gray-900 bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
                                                                        <input type="checkbox" checked={section.enabled} onChange={() => toggleSection(section.id)} className="w-3.5 h-3.5 text-pink-600 rounded focus:ring-pink-500 border-gray-300" />
                                                                        Visible
                                                                    </label>
                                                                    <div className="flex gap-2">
                                                                        <button onClick={() => duplicateSection(section.id)} className="bg-blue-50 text-blue-600 hover:bg-blue-100 px-2 py-1 rounded-md text-xs font-bold transition flex items-center gap-1">
                                                                            Copy
                                                                        </button>
                                                                        <button onClick={() => deleteSection(section.id)} className="bg-red-50 text-red-600 hover:bg-red-100 px-2 py-1 rounded-md text-xs font-bold transition flex items-center gap-1">
                                                                            Delete
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Bottom Wizard Navigation */}
                                    <div className="p-5 border-t border-gray-200 bg-white flex gap-3 flex-shrink-0">
                                        {editorStep > 1 && (
                                            <button onClick={() => setEditorStep(prev => prev - 1)} className="flex-1 bg-gray-100 border border-gray-200 text-gray-700 py-3 rounded-xl font-bold hover:bg-gray-200 transition shadow-sm text-sm">
                                                &larr; Back
                                            </button>
                                        )}
                                        {editorStep < 3 ? (
                                            <button onClick={() => setEditorStep(prev => prev + 1)} className="flex-[2] bg-gray-900 text-white py-3 rounded-xl font-bold hover:bg-black transition shadow-md text-sm">
                                                Next Step &rarr;
                                            </button>
                                        ) : (
                                            <button onClick={handlePublish} className="flex-[2] bg-pink-600 text-white py-3 rounded-xl font-bold hover:bg-pink-700 transition shadow-md text-sm">
                                                🚀 Publish Site
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </aside>

                            {/* MAIN PREVIEW PANEL */}
                            <main className={`flex-1 bg-gray-200 overflow-hidden relative flex flex-col pb-24 md:pb-0 ${mobileView === 'editor' ? 'hidden md:flex' : 'flex'}`}>
                                {/* INJECT STYLE FOR ANIMATION */}
                                <style dangerouslySetInnerHTML={{__html: `
                                    .wedding-animate-fade-in {
                                        animation: weddingFadeIn 0.8s ease-out forwards;
                                    }
                                    @keyframes weddingFadeIn {
                                        from { opacity: 0; transform: translateY(30px); }
                                        to { opacity: 1; transform: translateY(0); }
                                    }
                                `}} />
                                <div className="flex-1 overflow-y-auto custom-scrollbar relative p-0 md:p-6 lg:p-8">
                                    <div className="max-w-5xl mx-auto bg-white shadow-2xl overflow-hidden relative min-h-full md:rounded-2xl transition-all duration-300">
                                        {siteData.sections.map((section, index) => (
                                            <div 
                                                key={`${section.id}-${siteData.theme?.enableAnimations ? 'anim' : 'no'}`} 
                                                id={`preview-${section.id}`}
                                                className={`relative group ${!section.enabled ? 'opacity-50 grayscale' : ''}`}
                                                onClick={() => { 
                                                    if (activeSectionId !== section.id) {
                                                        setActiveSectionId(section.id); 
                                                        setMobileView('preview'); 
                                                    }
                                                }}
                                            >
                                                <div className={`absolute inset-0 pointer-events-none z-[40] transition-all ${activeSectionId === section.id ? 'ring-inset ring-[6px] ring-pink-500/50 bg-pink-500/5' : 'group-hover:bg-pink-500/10 group-hover:ring-inset group-hover:ring-[4px] group-hover:ring-pink-500/30 cursor-pointer'}`}></div>
                                                
                                                <div className={activeSectionId !== section.id ? "pointer-events-none" : ""}>
                                                    {renderSectionPreview(section, index)}
                                                </div>
                                                
                                                {/* INLINE EDITOR */}
                                                {activeSectionId === section.id && (
                                                    <div className="relative z-[60] bg-white border-t-4 border-pink-400 shadow-[0_-10px_20px_rgba(0,0,0,0.05)] cursor-default" onClick={(e) => e.stopPropagation()}>
                                                        <div className="flex justify-end p-2 bg-gray-50 border-b border-gray-200">
                                                            <button onClick={(e) => { e.stopPropagation(); setActiveSectionId(null); }} className="text-gray-500 hover:text-gray-800 bg-gray-200 hover:bg-gray-300 rounded-full w-8 h-8 flex items-center justify-center font-bold transition shadow-sm">
                                                                ✕
                                                            </button>
                                                        </div>
                                                        <div>
                                                            {renderSectionEditor(section)}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </main>
                        </div>
                    </div>
                )}
                
                {/* 2. GUESTS TAB */}
                {hubTab === 'guests' && <WeddingGuestsManager siteData={siteData} db={db} />}
                
                {/* 3. CHECKLIST TAB */}
                {hubTab === 'checklist' && (
                    <div className="h-full overflow-y-auto custom-scrollbar bg-gray-50 p-4 md:p-8 animate-fade-in">
                        <div className="max-w-5xl mx-auto">
                            <Checklist user={user} db={db} navigateTo={navigateTo} eventId={siteData.id} />
                        </div>
                    </div>
                )}
                
                {/* 4. BUDGET TAB */}
                {hubTab === 'budget' && (
                    <div className="h-full overflow-y-auto custom-scrollbar bg-gray-50 p-4 md:p-8 animate-fade-in">
                        <div className="max-w-5xl mx-auto">
                            <BudgetTracker user={user} db={db} eventId={siteData.id} />
                        </div>
                    </div>
                )}
                
                {/* 5. RENTALS TAB */}
                {hubTab === 'rentals' && <WeddingRentalsManager siteData={siteData} user={user} db={db} navigateTo={navigateTo} />}
            </div>
        </div>
    );
}

// --- UPDATED RENDERERS ---

const getSizingClasses = (textSize) => {
    switch(textSize) {
        case 'small': return { 
            heroHeading: 'text-4xl md:text-5xl lg:text-6xl', 
            heroSub: 'text-base md:text-xl', 
            heading: 'text-3xl md:text-4xl', 
            body: 'text-base', 
            small: 'text-xs', 
            cardHeading: 'text-lg md:text-xl' 
        };
        case 'large': return { 
            heroHeading: 'text-6xl md:text-8xl lg:text-9xl', 
            heroSub: 'text-xl md:text-3xl', 
            heading: 'text-5xl md:text-6xl', 
            body: 'text-xl md:text-2xl', 
            small: 'text-base', 
            cardHeading: 'text-3xl md:text-4xl' 
        };
        case 'xlarge': return { 
            heroHeading: 'text-7xl md:text-9xl lg:text-[10rem]', 
            heroSub: 'text-2xl md:text-4xl', 
            heading: 'text-6xl md:text-7xl', 
            body: 'text-2xl md:text-3xl', 
            small: 'text-lg', 
            cardHeading: 'text-4xl md:text-5xl' 
        };
        default: return { 
            heroHeading: 'text-5xl md:text-7xl lg:text-8xl', 
            heroSub: 'text-lg md:text-2xl', 
            heading: 'text-4xl md:text-5xl', 
            body: 'text-lg', 
            small: 'text-sm', 
            cardHeading: 'text-2xl md:text-3xl' 
        };
    }
};

// --- NEW: Cash Fund Contribution Modal ---
function CashFundContributionModal({ fund, theme, onClose, weddingId }) {
    const [amount, setAmount] = useState('');
    const [guestName, setGuestName] = useState('');
    const [message, setMessage] = useState('');
    const [step, setStep] = useState('details'); // 'details' -> 'processing' -> 'success'
    
    const presetAmounts = [50, 100, 150, 200];
    const themeColor = theme?.primaryColor || '#ec4899';

    const handleContribute = async (e) => {
        e.preventDefault();
        const numAmount = parseFloat(amount);
        if (!guestName || isNaN(numAmount) || numAmount <= 0) return;

        setStep('processing');
        
        try {
            // 1. Update the total amount on the specific fund in the wedding document
            const weddingRef = doc(db, 'weddings', weddingId);
            const snap = await getDoc(weddingRef);
            if (snap.exists()) {
                const data = snap.data();
                const newSections = data.sections.map(sec => {
                    if (sec.type === 'registry' && sec.content.cashFunds) {
                        return {
                            ...sec,
                            content: {
                                ...sec.content,
                                cashFunds: sec.content.cashFunds.map(f => 
                                    f.id === fund.id 
                                    ? { ...f, currentAmount: (f.currentAmount || 0) + numAmount } 
                                    : f
                                )
                            }
                        };
                    }
                    return sec;
                });
                
                await updateDoc(weddingRef, { sections: newSections });
                
                // 2. Log the individual contribution for the couple to view later
                await addDoc(collection(db, `weddings/${weddingId}/contributions`), {
                    fundId: fund.id,
                    fundTitle: fund.title,
                    amount: numAmount,
                    guestName: guestName.trim(),
                    message: message.trim(),
                    createdAt: serverTimestamp()
                });

                // Simulate processing delay for realistic UX
                setTimeout(() => {
                    setStep('success');
                }, 1500);
            }
        } catch (error) {
            console.error("Contribution failed:", error);
            alert("Payment failed to process. Please try again.");
            setStep('details');
        }
    };

    return (
        <div className="fixed inset-0 z-[500] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden relative" onClick={(e) => e.stopPropagation()}>
                
                {/* Header Image */}
                <div className="h-32 md:h-40 relative bg-gray-100">
                    <img src={fund.imageUrl || 'https://placehold.co/600x300/fdf2f8/db2777?text=Fund'} alt={fund.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                    <button onClick={onClose} className="absolute top-4 right-4 bg-black/30 hover:bg-black/50 backdrop-blur-md text-white w-8 h-8 rounded-full flex items-center justify-center transition">✕</button>
                    <div className="absolute bottom-4 left-5 text-white">
                        <p className="text-[10px] font-bold uppercase tracking-widest opacity-80 mb-1">Contribute to</p>
                        <h3 className="font-serif text-2xl font-bold leading-tight">{fund.title}</h3>
                    </div>
                </div>

                <div className="p-6 md:p-8">
                    {step === 'details' && (
                        <form onSubmit={handleContribute} className="space-y-5 animate-fade-in">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Select Amount</label>
                                <div className="grid grid-cols-4 gap-2 mb-3">
                                    {presetAmounts.map(preset => (
                                        <button 
                                            key={preset}
                                            type="button"
                                            onClick={() => setAmount(preset.toString())}
                                            className={`py-2 rounded-xl text-sm font-bold border-2 transition-all ${amount === preset.toString() ? 'border-transparent text-white shadow-md' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                                            style={amount === preset.toString() ? { backgroundColor: themeColor } : {}}
                                        >
                                            ${preset}
                                        </button>
                                    ))}
                                </div>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">$</span>
                                    <input 
                                        type="number" 
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        placeholder="Other Amount" 
                                        className="w-full pl-8 p-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white outline-none transition font-bold text-gray-900"
                                        style={{ '--tw-ring-color': themeColor }}
                                    />
                                </div>
                            </div>
                            
                            <div className="border-t border-gray-100 pt-5 space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Your Name</label>
                                    <input type="text" required value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="How should we address the card?" className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': themeColor }} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Message (Optional)</label>
                                    <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Leave a warm wish for the couple..." rows="2" className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white outline-none transition resize-none text-sm" style={{ '--tw-ring-color': themeColor }}></textarea>
                                </div>
                            </div>

                            <button type="submit" disabled={!amount || parseFloat(amount) <= 0 || !guestName} className="w-full py-4 rounded-xl font-bold text-white shadow-lg transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:transform-none text-lg mt-4" style={{ backgroundColor: themeColor }}>
                                Continue to Payment
                            </button>
                        </form>
                    )}

                    {step === 'processing' && (
                        <div className="py-12 flex flex-col items-center justify-center text-center animate-fade-in">
                            <div className="w-16 h-16 border-4 border-gray-200 border-t-pink-500 rounded-full animate-spin mb-6" style={{ borderTopColor: themeColor }}></div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Processing Contribution</h3>
                            <p className="text-sm text-gray-500">Please wait while we secure your transaction securely...</p>
                        </div>
                    )}

                    {step === 'success' && (
                        <div className="py-8 text-center animate-fade-in">
                            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner" style={{ backgroundColor: `${themeColor}20`, color: themeColor }}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 font-serif mb-3">Thank You!</h3>
                            <p className="text-gray-600 mb-8 leading-relaxed">Your generous gift of <strong className="text-gray-900">${parseFloat(amount).toFixed(2)}</strong> has been successfully added to the {fund.title}.</p>
                            <button onClick={onClose} className="w-full py-3.5 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition shadow-md">
                                Return to Registry
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function RegistrySection({ content, theme, weddingId }) {
    const sizes = getSizingClasses(content.textSize);
    const fontFam = content.fontFamily || theme.fontFamily;
    const [selectedFund, setSelectedFund] = useState(null);

    return (
        <div className="py-20 px-4 border-b border-gray-100" style={{ backgroundColor: content.bgColor || theme.backgroundColor, color: content.textColor || 'inherit' }}>
            <div className="container mx-auto max-w-5xl">
                <h2 className={`${sizes.heading} font-bold mb-6 font-serif text-center`} style={{ fontFamily: fontFam, color: content.textColor || theme.primaryColor }}>{content.heading}</h2>
                <p className={`${sizes.body} mb-12 max-w-2xl mx-auto leading-relaxed text-center ${content.textColor ? '' : 'text-gray-600'}`}>Your presence at our wedding is the greatest gift of all. However, if you wish to honor us with a gift, we have registered at the following stores and funds.</p>
                
                {/* NEW: Native Cash Funds Rendering */}
                {(content.cashFunds || []).length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                        {content.cashFunds.map((fund, idx) => {
                            const current = fund.currentAmount || 0;
                            const goal = parseFloat(fund.goalAmount) || 0;
                            const progress = goal > 0 ? Math.min(100, (current / goal) * 100) : 0;

                            return (
                                <div key={fund.id || idx} className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden flex flex-col group hover:shadow-xl transition-shadow text-gray-900">
                                    <div className="h-48 relative overflow-hidden bg-gray-100">
                                        <img src={fund.imageUrl || 'https://placehold.co/600x400/fdf2f8/db2777?text=Fund'} alt={fund.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                                        <div className="absolute bottom-4 left-5 right-5">
                                            <h3 className={`font-serif font-bold text-white drop-shadow-md truncate ${sizes.cardHeading}`}>{fund.title}</h3>
                                        </div>
                                    </div>
                                    <div className="p-6 md:p-8 flex flex-col flex-grow">
                                        <p className="text-gray-600 mb-6 leading-relaxed flex-grow text-sm">{fund.description}</p>
                                        
                                        <div className="mb-6">
                                            <div className="flex justify-between items-end mb-2">
                                                <span className="text-2xl font-black text-gray-900">${current.toLocaleString()}</span>
                                                {goal > 0 && <span className="text-sm font-bold text-gray-400 uppercase tracking-widest">Goal: ${goal.toLocaleString()}</span>}
                                            </div>
                                            {goal > 0 && (
                                                <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden">
                                                    <div className="h-full rounded-full transition-all duration-1000 ease-out" style={{ width: `${progress}%`, backgroundColor: theme.primaryColor }}></div>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <button 
                                            onClick={() => setSelectedFund(fund)}
                                            className="w-full py-3.5 rounded-xl font-bold text-white shadow-md hover:opacity-90 transition transform hover:-translate-y-0.5" 
                                            style={{ backgroundColor: theme.primaryColor }}
                                        >
                                            Contribute
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Universal Registry / Physical Gifts */}
                {(content.physicalGifts || []).length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 justify-center mb-12">
                        {content.physicalGifts.map((gift, idx) => (
                            <a key={idx} href={gift.url} target="_blank" rel="noopener noreferrer" className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-gray-900">
                                <div className="h-48 relative overflow-hidden bg-white flex items-center justify-center p-4 border-b border-gray-50">
                                    {gift.imageUrl ? (
                                        <img src={gift.imageUrl} alt={gift.title} className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                                    ) : (
                                        <span className="text-5xl">🎁</span>
                                    )}
                                </div>
                                <div className="p-6 flex flex-col flex-grow text-center">
                                    <h3 className={`font-bold text-gray-900 mb-2 line-clamp-2 ${sizes.small}`}>{gift.title || 'Special Gift'}</h3>
                                    <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-4">{gift.storeName || 'Online Store'}</p>
                                    
                                    <div className="mt-auto flex flex-col gap-3">
                                        {gift.price && <p className="text-lg font-black text-gray-900">${gift.price}</p>}
                                        <span className="w-full py-3 rounded-xl font-bold text-white shadow-sm transition group-hover:opacity-90 text-sm" style={{ backgroundColor: theme.primaryColor }}>
                                            View Item
                                        </span>
                                    </div>
                                </div>
                            </a>
                        ))}
                    </div>
                )}

                {/* External Registry Links */}
                {(content.links || []).length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 justify-center">
                        {content.links.map((link, idx) => (
                            <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 transform group flex flex-col items-center justify-center gap-4 text-gray-900">
                                <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gray-50 group-hover:bg-pink-50 transition-colors text-2xl" style={{ color: theme.primaryColor }}>
                                    🎁
                                </div>
                                <span className={`font-bold text-gray-800 tracking-tight text-center ${sizes.cardHeading}`}>{link.storeName || 'Our Registry'}</span>
                                <span className={`${sizes.small} font-bold uppercase tracking-widest text-gray-400 group-hover:text-pink-500 transition-colors flex items-center gap-1`}>
                                    Shop Now <span className="group-hover:translate-x-1 transition-transform">→</span>
                                </span>
                            </a>
                        ))}
                    </div>
                )}

                {(!content.links || content.links.length === 0) && (!content.cashFunds || content.cashFunds.length === 0) && (!content.physicalGifts || content.physicalGifts.length === 0) && (
                    <div className="col-span-full py-12 text-gray-400 italic text-center">No registries added yet.</div>
                )}
            </div>

            {/* Render Modal */}
            {selectedFund && (
                <CashFundContributionModal 
                    fund={selectedFund} 
                    theme={theme} 
                    onClose={() => setSelectedFund(null)} 
                    weddingId={weddingId}
                />
            )}
        </div>
    );
}

// NEW: TravelSection
function TravelSection({ content, theme }) {
    const sizes = getSizingClasses(content.textSize);
    const fontFam = content.fontFamily || theme.fontFamily;
    return (
        <div className="py-20 px-4 border-b border-gray-100" style={{ backgroundColor: content.bgColor || theme.backgroundColor, color: content.textColor || 'inherit' }}>
            <div className="container mx-auto max-w-5xl">
                <h2 className={`${sizes.heading} font-bold text-center mb-12 font-serif`} style={{ fontFamily: fontFam, color: content.textColor || theme.primaryColor }}>{content.heading}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {(content.accommodations || []).map((acc, idx) => (
                        <div key={idx} className="bg-white p-8 md:p-10 rounded-3xl shadow-lg border border-gray-100 flex flex-col h-full relative overflow-hidden group text-gray-900">
                            <div className="absolute top-0 left-0 w-full h-2 transition-colors" style={{ backgroundColor: theme.primaryColor, opacity: 0.8 }}></div>
                            <h3 className={`${sizes.cardHeading} font-bold text-gray-900 mb-3 font-serif`} style={{ fontFamily: fontFam }}>{acc.name || 'Hotel Name'}</h3>
                            <p className={`${sizes.body} text-gray-500 mb-6 font-medium flex items-center gap-2`}><span className="text-xl opacity-80">📍</span> {acc.address || 'Location Address'}</p>
                            <p className={`${sizes.body} text-gray-600 leading-relaxed mb-8 flex-grow whitespace-pre-wrap`}>{acc.details || 'Details about the room block or travel logistics.'}</p>
                            {acc.bookingUrl && (
                                <a href={acc.bookingUrl} target="_blank" rel="noopener noreferrer" className={`inline-block w-full text-center py-4 rounded-full font-bold text-white transition-all transform hover:-translate-y-1 shadow-md hover:shadow-lg ${sizes.body}`} style={{ backgroundColor: theme.primaryColor }}>
                                    Book a Room
                                </a>
                            )}
                        </div>
                    ))}
                    {(!content.accommodations || content.accommodations.length === 0) && (
                        <div className="col-span-full py-12 text-center text-gray-400 italic">No travel details added yet.</div>
                    )}
                </div>
            </div>
        </div>
    );
}

// UPDATED: GallerySection
function GallerySection({ content, theme }) {
    const sizes = getSizingClasses(content.textSize);
    const fontFam = content.fontFamily || theme.fontFamily;
    return (
        <div className="py-20 px-4 border-b border-gray-100" style={{ backgroundColor: content.bgColor || theme.backgroundColor, color: content.textColor || 'inherit' }}>
            <div className="container mx-auto max-w-6xl">
                <h2 className={`${sizes.heading} font-bold text-center font-serif mb-16`} style={{ fontFamily: fontFam, color: content.textColor || theme.primaryColor }}>{content.heading}</h2>
                <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
                    {(content.images || []).map((img, idx) => (
                        <div key={idx} className="break-inside-avoid overflow-hidden group relative shadow-md rounded-2xl">
                            <img src={img.url} alt="Gallery" className="w-full h-auto object-cover transform group-hover:scale-105 transition duration-700 ease-in-out bg-gray-200" />
                            {img.caption && (
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition duration-300 flex items-end justify-center p-6">
                                    <p className={`text-white text-center font-medium drop-shadow-md ${sizes.body}`}>{img.caption}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                {(!content.images || content.images.length === 0) && (
                    <div className="text-center text-gray-400 italic">Upload images to display your gallery.</div>
                )}
            </div>
        </div>
    );
}

function HeroSection({ content, theme, isEditing, onFieldChange }) {
    const sizes = getSizingClasses(content.textSize);
    const fontFam = content.fontFamily || theme.fontFamily;
    
    // NEW: Countdown Timer Logic
    const [timeLeft, setTimeLeft] = useState(null);

    useEffect(() => {
        if (!content.showCountdown || !content.date) return;

        const calculateTimeLeft = () => {
            const difference = +new Date(content.date.replace(/-/g, '/')) - +new Date();
            if (difference > 0) {
                return {
                    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                    mins: Math.floor((difference / 1000 / 60) % 60)
                };
            }
            return { days: 0, hours: 0, mins: 0 };
        };

        setTimeLeft(calculateTimeLeft());
        const timer = setInterval(() => {
            setTimeLeft(calculateTimeLeft());
        }, 60000);

        return () => clearInterval(timer);
    }, [content.date, content.showCountdown]);
    
    const backgroundStyle = content.imageUrl 
        ? { backgroundImage: `url(${content.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } 
        : { backgroundColor: content.bgColor || theme.backgroundColor || theme.primaryColor };
    
    const textColorVal = content.textColor || theme.primaryColor;
    
    // NEW: Capture custom overlay settings with fallbacks to defaults
    const overlayColor = content.overlayColor || '#000000';
    const overlayOpacity = content.overlayOpacity !== undefined ? content.overlayOpacity : 0.4;
    
    const cardStyle = content.cardStyle || 'glass';
    let cardClasses = 'relative z-10 p-10 md:p-16 max-w-4xl w-full mx-4 animate-fade-in-up';
    if (cardStyle === 'glass') {
        cardClasses += ' bg-white/95 backdrop-blur-md rounded-[2.5rem] shadow-2xl border border-white/50';
    } else if (cardStyle === 'solid') {
        cardClasses += ' bg-white rounded-[2.5rem] shadow-xl border border-gray-100';
    }

    const hAlign = content.horizontalAlign || 'center';
    const vAlign = content.verticalAlign || 'center';

    const justifyClass = hAlign === 'left' ? 'justify-start' : hAlign === 'right' ? 'justify-end' : 'justify-center';
    const itemsClass = vAlign === 'top' ? 'items-start pt-16 md:pt-24' : vAlign === 'bottom' ? 'items-end pb-16 md:pb-24' : 'items-center py-24';
    const textAlignClass = hAlign === 'left' ? 'text-left' : hAlign === 'right' ? 'text-right' : 'text-center';
    const lineAlign = hAlign === 'left' ? 'ml-0' : hAlign === 'right' ? 'mr-0' : 'mx-auto';

    return (
        <div className={`relative flex ${itemsClass} ${justifyClass} ${textAlignClass} min-h-[70vh] md:min-h-[85vh] px-4 overflow-hidden border-b border-gray-100`} style={backgroundStyle}>
            {content.imageUrl && <div className="absolute inset-0" style={{ backgroundColor: overlayColor, opacity: overlayOpacity }}></div>}
            <div className={cardClasses}>
                {isEditing ? (
                    <div className="space-y-6">
                        <input
                            type="text"
                            value={content.title || ''}
                            onChange={(e) => onFieldChange('title', e.target.value)}
                            className={`${sizes.heroHeading} font-bold bg-transparent w-full outline-none border-b-2 border-dashed placeholder-gray-400 ${textAlignClass}`}
                            style={{ fontFamily: fontFam, color: textColorVal, borderColor: textColorVal }}
                            placeholder="Couple Names"
                        />
                        <input
                            type="date"
                            value={content.date || ''}
                            onChange={(e) => onFieldChange('date', e.target.value)}
                            className={`${sizes.heroSub} mt-4 bg-transparent w-full outline-none border-b-2 border-dashed tracking-widest uppercase font-semibold ${textAlignClass}`}
                            style={{ color: content.textColor || (cardStyle === 'transparent' ? textColorVal : '#4b5563'), borderColor: content.textColor || '#d1d5db' }}
                        />
                        <input
                            type="text"
                            value={content.city || ''}
                            onChange={(e) => onFieldChange('city', e.target.value)}
                            className={`${sizes.heroSub} mt-4 bg-transparent w-full outline-none border-b-2 border-dashed tracking-widest uppercase font-semibold ${textAlignClass}`}
                            style={{ color: content.textColor || (cardStyle === 'transparent' ? textColorVal : '#4b5563'), borderColor: content.textColor || '#d1d5db' }}
                            placeholder="City, State (Optional)"
                        />
                    </div>
                ) : (
                    <div className="space-y-6">
                        <h1 className={`${sizes.heroHeading} font-bold tracking-tight leading-tight`} style={{ fontFamily: fontFam, color: textColorVal }}>{content.title}</h1>
                        <div className={`h-px w-24 my-6 ${lineAlign} ${cardStyle === 'transparent' ? 'bg-current opacity-50' : 'bg-gradient-to-r from-transparent via-gray-300 to-transparent'}`}></div>
                        <p className={`${sizes.heroSub} tracking-widest uppercase font-semibold`} style={{ color: content.textColor || (cardStyle === 'transparent' ? textColorVal : '#4b5563') }}>
                            {content.date ? new Date(content.date.replace(/-/g, '\/')).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Date TBD'}
                            {content.city && <span className="block mt-2">{content.city}</span>}
                        </p>
                        
                        {content.showCountdown && timeLeft && (
                            <div className="flex gap-4 md:gap-8 mt-8 border-t pt-8 justify-center animate-fade-in" style={{ borderColor: content.textColor ? `${content.textColor}40` : 'rgba(0,0,0,0.1)' }}>
                                <div className="flex flex-col items-center">
                                    <span className="text-3xl md:text-5xl font-light tabular-nums" style={{ color: textColorVal }}>{timeLeft.days}</span>
                                    <span className="text-[10px] md:text-xs tracking-widest uppercase font-bold mt-1 opacity-70" style={{ color: content.textColor || '#4b5563' }}>Days</span>
                                </div>
                                <div className="flex flex-col items-center">
                                    <span className="text-3xl md:text-5xl font-light tabular-nums" style={{ color: textColorVal }}>{timeLeft.hours}</span>
                                    <span className="text-[10px] md:text-xs tracking-widest uppercase font-bold mt-1 opacity-70" style={{ color: content.textColor || '#4b5563' }}>Hours</span>
                                </div>
                                <div className="flex flex-col items-center">
                                    <span className="text-3xl md:text-5xl font-light tabular-nums" style={{ color: textColorVal }}>{timeLeft.mins}</span>
                                    <span className="text-[10px] md:text-xs tracking-widest uppercase font-bold mt-1 opacity-70" style={{ color: content.textColor || '#4b5563' }}>Mins</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function StorySection({ content, theme, isEditing, onFieldChange }) {
    const sizes = getSizingClasses(content.textSize);
    const fontFam = content.fontFamily || theme.fontFamily;
     return (
        <div className="py-20 px-4 border-b border-gray-100" style={{ backgroundColor: content.bgColor || theme.backgroundColor, color: content.textColor || 'inherit' }}>
            <div className="container mx-auto max-w-6xl">
                <div className={`flex flex-col md:flex-row items-center gap-12 lg:gap-20 ${content.imageUrl ? '' : 'justify-center text-center'}`}>
                    {content.imageUrl && (
                        <div className="w-full md:w-1/2">
                            <div className="relative p-4 max-w-md mx-auto md:max-w-none">
                                <div className="absolute inset-0 border-4 translate-x-4 translate-y-4 rounded-2xl transition-all" style={{ borderColor: content.textColor || theme.primaryColor, opacity: 0.4 }}></div>
                                <img src={content.imageUrl} alt="Our Story" className="relative w-full h-auto object-cover rounded-2xl shadow-xl z-10 aspect-[3/4]" />
                            </div>
                        </div>
                    )}
                    <div className={`w-full ${content.imageUrl ? 'md:w-1/2' : 'md:w-3/4'} space-y-8`}>
                        {isEditing ? (
                            <input
                                type="text"
                                value={content.heading || ''}
                                onChange={(e) => onFieldChange('heading', e.target.value)}
                                className={`${sizes.heading} font-bold mb-6 w-full bg-transparent outline-none border-b-2 border-dashed`}
                                style={{ fontFamily: fontFam, color: content.textColor || theme.primaryColor, borderColor: content.textColor || theme.primaryColor }}
                                placeholder="Heading"
                            />
                        ) : (
                            <h2 className={`${sizes.heading} font-bold font-serif leading-tight`} style={{ fontFamily: fontFam, color: content.textColor || theme.primaryColor }}>{content.heading}</h2>
                        )}
                        
                        {isEditing ? (
                            <textarea
                                value={content.text || ''}
                                onChange={(e) => onFieldChange('text', e.target.value)}
                                className={`text-gray-700 whitespace-pre-wrap leading-relaxed w-full h-64 p-4 border rounded-xl focus:ring-2 outline-none ${sizes.body}`}
                                style={content.textColor ? { color: content.textColor, backgroundColor: 'rgba(255,255,255,0.8)' } : {}}
                                placeholder="Write your story..."
                            />
                        ) : (
                            <p className={`whitespace-pre-wrap leading-relaxed font-light opacity-90 ${content.textColor ? '' : 'text-gray-700'} ${sizes.body}`} style={content.textColor ? { color: content.textColor } : {}}>{content.text}</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
     );
}

function EventsSection({ content, theme }) {
    const sizes = getSizingClasses(content.textSize);
    const fontFam = content.fontFamily || theme.fontFamily;
    return (
        <div className="py-20 px-4 border-b border-gray-100 relative overflow-hidden" style={{ backgroundColor: content.bgColor || theme.backgroundColor, color: content.textColor || 'inherit' }}>
            {/* Subtle background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-black opacity-5 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2 pointer-events-none"></div>
            
            <div className="container mx-auto max-w-5xl relative z-10">
                <h2 className={`${sizes.heading} font-bold text-center mb-16 font-serif`} style={{ fontFamily: fontFam, color: content.textColor || theme.primaryColor }}>{content.heading}</h2>
                
                <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-200 before:to-transparent">
                    {(content.eventsList || []).map((evt, idx) => (
                        <div key={idx} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group">
                            {/* Timeline Icon Marker */}
                            <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-white bg-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2" style={{ color: theme.primaryColor }}>
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"></path></svg>
                            </div>
                            {/* Event Card */}
                            <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:-translate-y-1 transition transform duration-300 text-gray-900">
                                <p className="text-sm font-bold uppercase tracking-widest mb-1" style={{ color: theme.primaryColor }}>{evt.time}</p>
                                <h3 className={`${sizes.cardHeading} font-bold mb-2 font-serif`} style={{ fontFamily: fontFam }}>{evt.title}</h3>
                                <p className={`${sizes.small} text-gray-500 font-medium mb-3 flex items-center gap-1.5`}>
                                    <span className="opacity-70">📍</span> {evt.location}
                                </p>
                                <p className={`${sizes.body} text-gray-600 leading-relaxed`}>{evt.description}</p>
                            </div>
                        </div>
                    ))}
                    {(!content.eventsList || content.eventsList.length === 0) && (
                        <div className="text-center py-12 text-gray-400 italic">No events scheduled yet.</div>
                    )}
                </div>
            </div>
        </div>
    );
}

function RsvpSection({ content, theme, weddingId, isEditing }) {
    const sizes = getSizingClasses(content.textSize);
    const fontFam = content.fontFamily || theme.fontFamily;
    
    const [partyMembers, setPartyMembers] = useState([
        { id: Date.now(), name: '', email: '', isAttending: 'yes', mealChoice: '', dietary: '' }
    ]);
    const [submitted, setSubmitted] = useState(false);

    const isPastDeadline = content.deadline && new Date(content.deadline) < new Date(new Date().setHours(0,0,0,0));
    const maxPartySize = content.maxPartySize || 10;
    const canAddMore = partyMembers.length < maxPartySize;

    const handleAddMember = () => {
        setPartyMembers([...partyMembers, { id: Date.now(), name: '', email: '', isAttending: 'yes', mealChoice: '', dietary: '' }]);
    };

    const handleMemberChange = (id, field, value) => {
        setPartyMembers(prev => prev.map(m => m.id === id ? { ...m, [field]: value } : m));
    };

    const handleRemoveMember = (id) => {
        setPartyMembers(prev => prev.filter(m => m.id !== id));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Ensure all members have names
        if (partyMembers.some(m => !m.name.trim())) return;
        
        await addDoc(collection(db, `weddings/${weddingId}/rsvps`), {
            partyMembers: partyMembers.map(m => ({
                name: m.name.trim(),
                email: m.email ? m.email.trim() : '',
                isAttending: m.isAttending === 'yes',
                mealChoice: m.mealChoice || '',
                dietary: m.dietary || ''
            })),
            submittedAt: serverTimestamp()
        });
        setSubmitted(true);
    };

    return (
        <div className="py-24 border-t-8 border-transparent" style={{ backgroundColor: content.bgColor || theme.backgroundColor, borderTopColor: theme.primaryColor, color: content.textColor || 'inherit' }}>
            <div className="container mx-auto p-4 md:p-8 max-w-2xl bg-white shadow-2xl rounded-3xl border border-gray-100 relative overflow-hidden text-gray-900">
                <div className="absolute top-0 left-0 w-full h-2" style={{ backgroundColor: theme.primaryColor }}></div>
                <h2 className={`${sizes.heading} font-bold text-center mb-8 font-serif`} style={{ fontFamily: fontFam, color: content.textColor || theme.primaryColor }}>{content.heading}</h2>
                {submitted ? (
                    <div className="text-center py-12 animate-fade-in">
                        <div className="text-5xl mb-4">✨</div>
                        <p className={`font-bold text-gray-900 ${sizes.cardHeading}`}>Thank you!</p>
                        <p className={`text-gray-600 mt-2 ${sizes.body}`}>Your response has been received.</p>
                    </div>
                ) : isPastDeadline && !isEditing ? (
                    <div className="text-center py-12 animate-fade-in bg-gray-50 rounded-2xl border border-gray-200 m-4">
                        <div className="text-5xl mb-4">⌛</div>
                        <p className={`font-bold text-gray-900 ${sizes.cardHeading}`}>RSVPs are closed</p>
                        <p className={`text-gray-600 mt-2 ${sizes.body}`}>The deadline to RSVP was {new Date(content.deadline).toLocaleDateString()}. Please contact the host directly if you need to make changes.</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        
                        <div className="space-y-6">
                            {partyMembers.map((member, index) => (
                                <div key={member.id} className="bg-gray-50 p-5 rounded-2xl border border-gray-200 relative animate-fade-in">
                                    {index > 0 && (
                                        <button type="button" onClick={() => handleRemoveMember(member.id)} className="absolute top-4 right-4 text-gray-400 hover:text-red-500 font-bold text-lg leading-none transition-colors">✕</button>
                                    )}
                                    <h4 className={`font-bold text-gray-800 mb-4 uppercase tracking-wider ${sizes.small}`}>Guest {index + 1}</h4>
                                    
                                    <div className="space-y-4">
                                        <input 
                                            type="text" 
                                            required 
                                            value={member.name} 
                                            onChange={e => handleMemberChange(member.id, 'name', e.target.value)} 
                                            placeholder="Full Name *" 
                                            className={`w-full p-3.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 transition ${sizes.body}`} 
                                            style={{ '--tw-ring-color': theme.primaryColor }} 
                                        />
                                        <input 
                                            type="email" 
                                            value={member.email} 
                                            onChange={e => handleMemberChange(member.id, 'email', e.target.value)} 
                                            placeholder="Email Address (Optional)" 
                                            className={`w-full p-3.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 transition ${sizes.body}`} 
                                            style={{ '--tw-ring-color': theme.primaryColor }} 
                                        />
                                        
                                        <div className="flex gap-4 items-center">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="radio" name={`attending-${member.id}`} checked={member.isAttending === 'yes'} onChange={() => handleMemberChange(member.id, 'isAttending', 'yes')} className="w-5 h-5 text-pink-600 focus:ring-pink-500" />
                                                <span className={`font-medium ${sizes.body}`}>Joyfully Accept</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="radio" name={`attending-${member.id}`} checked={member.isAttending === 'no'} onChange={() => handleMemberChange(member.id, 'isAttending', 'no')} className="w-5 h-5 text-pink-600 focus:ring-pink-500" />
                                                <span className={`font-medium text-gray-600 ${sizes.body}`}>Regretfully Decline</span>
                                            </label>
                                        </div>

                                        {member.isAttending === 'yes' && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-fade-in">
                                                <input 
                                                    type="text" 
                                                    value={member.mealChoice} 
                                                    onChange={e => handleMemberChange(member.id, 'mealChoice', e.target.value)} 
                                                    placeholder="Meal Preference (e.g. Chicken, Vegan)" 
                                                    className={`w-full p-3.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 transition ${sizes.body}`} 
                                                    style={{ '--tw-ring-color': theme.primaryColor }} 
                                                />
                                                <input 
                                                    type="text" 
                                                    value={member.dietary} 
                                                    onChange={e => handleMemberChange(member.id, 'dietary', e.target.value)} 
                                                    placeholder="Dietary restrictions / Allergies" 
                                                    className={`w-full p-3.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 transition ${sizes.body}`} 
                                                    style={{ '--tw-ring-color': theme.primaryColor }} 
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {canAddMore && (
                            <div className="pt-2">
                                <button type="button" onClick={handleAddMember} className="font-bold flex items-center justify-center gap-2 w-full py-4 rounded-xl border-2 border-dashed bg-gray-50 hover:bg-gray-100 transition-colors text-gray-600" style={{ borderColor: theme.primaryColor, color: theme.primaryColor }}>
                                    <span className="text-xl leading-none">+</span> Add another guest to this RSVP
                                </button>
                            </div>
                        )}

                        <button type="submit" className={`w-full text-white py-4 rounded-xl font-bold hover:shadow-lg transform hover:-translate-y-1 transition-all mt-6 shadow-md ${sizes.body}`} style={{backgroundColor: theme.primaryColor}}>Submit RSVP</button>
                    </form>
                )}
            </div>
        </div>
    );
}

function WeddingSitePublicView({ subdomain, isAuthReady }) {
    const [siteData, setSiteData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // NEW: State for showing the Save the Date Modal
    const [showSaveTheDate, setShowSaveTheDate] = useState(false);
    // NEW: Password Auth State
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [passwordInput, setPasswordInput] = useState('');

    useEffect(() => {
        if (!isAuthReady) {
            signInAnonymously(auth).catch(err => console.error("Anonymous sign-in failed", err));
            return;
        }

        const fetchSite = async () => {
            const q = query(collection(db, "weddings"), where("subdomain", "==", subdomain), limit(1));
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                const siteDoc = querySnapshot.docs[0];
                const data = siteDoc.data();
                if (data.isPublished) {
                    setSiteData({ id: siteDoc.id, ...data });
                    if (!data.isPasswordProtected) {
                        setIsAuthenticated(true);
                    }
                } else {
                    setError("This website is not yet published.");
                }
            } else {
                setError("Oops! We couldn't find a wedding website at this address.");
            }
            setLoading(false);
        };
        fetchSite();
    }, [subdomain, isAuthReady]);

    useEffect(() => {
        if (!siteData) return;
        
        const fontsToLoad = new Set();
        if (siteData.theme?.fontFamily) fontsToLoad.add(siteData.theme.fontFamily);
        (siteData.sections || []).forEach(sec => {
            if (sec.content?.fontFamily) fontsToLoad.add(sec.content.fontFamily);
        });

        fontsToLoad.forEach(fontFamily => {
            const font = fontFamily.replace(/'/g, "").split(',')[0].trim().replace(/ /g, "+");
            if (!font) return;
            const linkId = `google-font-${font.toLowerCase()}`;
            let link = document.getElementById(linkId);
            if (!link) {
                link = document.createElement('link');
                link.id = linkId;
                link.rel = 'stylesheet';
                link.href = `https://fonts.googleapis.com/css?family=${font}:400,700&display=swap`;
                document.head.appendChild(link);
            }
        });

        // NEW: Inject SEO tags if present
        if (siteData.seoTitle || siteData.seoDescription) {
            document.title = siteData.seoTitle || siteData.title || "Wedding Website";
            const metaDesc = document.querySelector('meta[name="description"]');
            if (metaDesc) {
                metaDesc.setAttribute("content", siteData.seoDescription || "");
            } else {
                const meta = document.createElement('meta');
                meta.name = "description";
                meta.content = siteData.seoDescription || "";
                document.head.appendChild(meta);
            }
        }
    }, [siteData?.theme?.fontFamily, siteData?.sections, siteData?.seoTitle, siteData?.seoDescription]);

    const handlePasswordSubmit = (e) => {
        e.preventDefault();
        if (siteData.password === passwordInput) {
            setIsAuthenticated(true);
        } else {
            alert("Incorrect password. Please try again.");
        }
    };

    const renderSection = (section, index) => {
        if (!section.enabled) return null;
        const props = {
            content: section.content,
            theme: siteData.theme,
            isEditing: false,
            weddingId: siteData.id
        };

        const animationClass = siteData.theme?.enableAnimations ? 'wedding-animate-fade-in' : '';
        const animationStyle = siteData.theme?.enableAnimations ? { animationFillMode: 'both', animationDuration: '0.8s', animationDelay: `${index * 150}ms` } : {};

        let SectionComponent = null;
        switch (section.type) {
            case 'hero': SectionComponent = <HeroSection key={section.id} {...props} />; break;
            case 'story': SectionComponent = <StorySection key={section.id} {...props} />; break;
            case 'events': SectionComponent = <EventsSection key={section.id} {...props} />; break;
            case 'travel': SectionComponent = <TravelSection key={section.id} {...props} />; break;
            case 'rsvp': SectionComponent = <RsvpSection key={section.id} {...props} />; break;
            case 'gallery': SectionComponent = <GallerySection key={section.id} {...props} />; break;
            case 'registry': SectionComponent = <RegistrySection key={section.id} {...props} />; break;
            default: return null;
        }

        return (
            <div key={`wrap-${section.id}-${siteData.theme?.enableAnimations ? 'anim' : 'no'}`} className={animationClass} style={animationStyle}>
                {SectionComponent}
            </div>
        );
    };

    if (loading) return <div className="text-center p-20">Loading Wedding Site...</div>;
    if (error) return <div className="text-center p-20 text-xl">{error}</div>;

    if (siteData.isPasswordProtected && !isAuthenticated) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" style={{ backgroundColor: siteData.theme?.backgroundColor || '#f9fafb' }}>
                <div className="max-w-md w-full bg-white p-8 rounded-3xl shadow-2xl text-center border border-gray-100 animate-fade-in-up">
                    <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl shadow-inner border border-gray-100">🔒</div>
                    <h2 className="text-3xl font-bold font-serif mb-3" style={{ fontFamily: siteData.theme?.fontFamily || 'inherit', color: siteData.theme?.primaryColor || '#111827' }}>Private Event</h2>
                    <p className="text-gray-500 mb-8 leading-relaxed">Please enter the access code provided by the couple to view their wedding website.</p>
                    <form onSubmit={handlePasswordSubmit} className="space-y-5">
                        <input type="password" value={passwordInput} onChange={e => setPasswordInput(e.target.value)} placeholder="Enter Access Code" required className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:bg-white transition text-center tracking-widest font-bold text-gray-800" style={{ '--tw-ring-color': siteData.theme?.primaryColor || '#ec4899' }} />
                        <button type="submit" className="w-full py-4 rounded-xl text-white font-bold shadow-lg hover:opacity-90 transition transform hover:-translate-y-0.5 text-lg" style={{ backgroundColor: siteData.theme?.primaryColor || '#ec4899' }}>Enter Site</button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className={`relative ${siteData.enableSaveTheDate ? 'pb-24 md:pb-20' : ''}`}>
            <style dangerouslySetInnerHTML={{__html: `
                .wedding-animate-fade-in {
                    animation: weddingFadeIn 0.8s ease-out forwards;
                }
                @keyframes weddingFadeIn {
                    from { opacity: 0; transform: translateY(30px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}} />

            {/* NEW: Sticky Navigation Menu */}
            <nav className="sticky top-0 z-[100] w-full bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm transition-all duration-300">
                <div className="container mx-auto px-4 md:px-8">
                    <ul className="flex items-center justify-start md:justify-center gap-6 md:gap-8 overflow-x-auto py-3.5 text-xs md:text-sm font-bold uppercase tracking-wider [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                        {siteData.sections.filter(s => s.enabled && !s.hideFromNav).map(section => {
                            let linkText = section.content.heading || section.content.title || section.type;
                            if (section.type === 'hero') linkText = 'Home';
                            
                            return (
                                <li key={`nav-${section.id}`} className="flex-shrink-0">
                                    <button 
                                        onClick={() => {
                                            const el = document.getElementById(section.id);
                                            if (el) {
                                                const y = el.getBoundingClientRect().top + window.scrollY - 55;
                                                window.scrollTo({ top: y, behavior: 'smooth' });
                                            }
                                        }}
                                        className="text-gray-600 hover:text-gray-900 transition-colors"
                                        onMouseEnter={(e) => e.target.style.color = siteData.theme?.primaryColor || '#ec4899'}
                                        onMouseLeave={(e) => e.target.style.color = ''}
                                    >
                                        {linkText}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </nav>

            {siteData.sections.map((section, index) => renderSection(section, index))}
            
            {/* NEW: Save the Date Floating Bar */}
            {siteData.enableSaveTheDate && (
                <div className="fixed bottom-0 left-0 w-full bg-white/95 backdrop-blur-md border-t border-gray-200 p-4 md:px-8 z-40 flex justify-between items-center shadow-[0_-10px_30px_rgba(0,0,0,0.1)] animate-fade-in-up">
                    <div className="hidden md:block">
                        <p className="font-bold text-gray-900 text-lg leading-tight">Save our Date!</p>
                        <p className="text-sm text-gray-600">Please share your mailing address so we can send you a formal invitation.</p>
                    </div>
                    <button onClick={() => setShowSaveTheDate(true)} className="w-full md:w-auto px-8 py-3 rounded-full font-bold text-white shadow-lg hover:opacity-90 transition-all transform hover:-translate-y-0.5" style={{ backgroundColor: siteData.theme?.primaryColor || '#ec4899' }}>
                        Add Details & Save the Date
                    </button>
                </div>
            )}
            
            {/* NEW: Save the Date Modal */}
            {showSaveTheDate && <SaveTheDateModal siteData={siteData} onClose={() => setShowSaveTheDate(false)} db={db} />}
        </div>
    );
}

// --- NEW: Save The Date Modal Component ---
function SaveTheDateModal({ siteData, onClose, db }) {
    const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', additionalGuests: 0, address1: '', address2: '', city: '', state: '', zip: '', country: 'US' });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const theme = siteData.theme || {};
    const primaryColor = theme.primaryColor || '#ec4899';
    const fontFamily = theme.fontFamily || 'inherit';

    const heroSection = siteData.sections.find(s => s.type === 'hero')?.content || {};
    const eventDate = heroSection.date || '';
    const eventTitle = siteData.title || 'Our Wedding';
    const heroImage = heroSection.imageUrl || null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: name === 'additionalGuests' ? (parseInt(value) || 0) : value }));
    };

    // Generate accurate Calendar links
    const getCalendarLinks = () => {
        if (!eventDate) return { google: '', ics: '', icsContent: '', yahoo: '' };
        
        // Force midday to avoid timezone shifting to previous day
        const start = new Date(`${eventDate}T12:00:00`);
        const end = new Date(`${eventDate}T12:00:00`);
        end.setDate(end.getDate() + 1); // All day event representation

        const formatDate = (d) => d.toISOString().replace(/-|:|\.\d\d\d/g, "").substring(0, 8);
        const startStr = formatDate(start);
        const endStr = formatDate(end);
        
        const details = `Please save the date for ${eventTitle}! Formal invitation to follow.`;
        
        const google = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventTitle)}&dates=${startStr}/${endStr}&details=${encodeURIComponent(details)}`;
        const yahoo = `https://calendar.yahoo.com/?v=60&view=d&type=20&title=${encodeURIComponent(eventTitle)}&st=${startStr}&et=${endStr}&desc=${encodeURIComponent(details)}`;
        
        const icsContent = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'BEGIN:VEVENT',
            `DTSTART;VALUE=DATE:${startStr}`,
            `DTEND;VALUE=DATE:${endStr}`,
            `SUMMARY:${eventTitle}`,
            `DESCRIPTION:${details}`,
            'END:VEVENT',
            'END:VCALENDAR'
        ].join('\n');
        
        const ics = `data:text/calendar;charset=utf8,${encodeURIComponent(icsContent)}`;
        
        return { google, ics, icsContent, yahoo };
    };

    const links = getCalendarLinks();

    const handleIcsClick = (e) => {
        e.preventDefault();
        const isSafari = typeof navigator !== 'undefined' && /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);
        const isAppleDevice = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);

        if (isAppleDevice && isSafari) {
             window.location.href = links.ics;
        } else {
             const blob = new Blob([links.icsContent], { type: 'text/calendar;charset=utf-8' });
             const url = window.URL.createObjectURL(blob);
             const link = document.createElement('a');
             link.href = url;
             link.setAttribute('download', 'save_the_date.ics');
             document.body.appendChild(link);
             link.click();
             document.body.removeChild(link);
             setTimeout(() => window.URL.revokeObjectURL(url), 100);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.firstName || !formData.lastName || !formData.email) return;
        setLoading(true);
        try {
            await addDoc(collection(db, `weddings/${siteData.id}/saveTheDates`), {
                ...formData,
                createdAt: serverTimestamp()
            });
            setSubmitted(true);
        } catch(err) {
            console.error("Save The Date Error:", err);
            alert("Failed to submit details. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 sm:p-6 animate-fade-in overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col md:flex-row overflow-hidden relative min-h-[500px] md:h-auto my-8 md:my-0">
                <button onClick={onClose} className="absolute top-3 right-3 md:top-4 md:right-4 z-[60] bg-white/90 hover:bg-white backdrop-blur-md rounded-full w-10 h-10 flex items-center justify-center text-gray-900 transition-colors shadow-md font-bold border border-gray-200 text-xl">×</button>
                
                {/* Left Side Image Panel */}
                <div className="w-full md:w-2/5 bg-gray-100 relative flex-shrink-0 min-h-[250px] md:min-h-full">
                    {heroImage ? (
                        <img src={heroImage} alt="Event Theme" className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                        <div className="absolute inset-0 w-full h-full" style={{ backgroundColor: primaryColor, opacity: 0.2 }}></div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex flex-col justify-end p-8 text-white">
                        <h3 className="text-3xl font-bold font-serif mb-2" style={{ fontFamily }}>{eventTitle}</h3>
                        {eventDate && <p className="text-lg font-medium opacity-90">{new Date(eventDate.replace(/-/g, '/')).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>}
                        <p className="text-xs opacity-70 mt-2 uppercase tracking-widest font-bold">Save the Date</p>
                    </div>
                </div>

                {/* Right Side Form Panel */}
                <div className="w-full md:w-3/5 p-6 md:p-10 bg-white flex flex-col justify-center">
                    {submitted ? (
                        <div className="text-center animate-fade-in space-y-6 py-8">
                            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto text-4xl text-green-500 shadow-inner">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                            </div>
                            <div>
                                <h3 className="text-3xl font-bold text-gray-900 font-serif mb-2" style={{ fontFamily }}>Thank you!</h3>
                                <p className="text-gray-600">We've securely saved your details. Formal invitation to follow.</p>
                            </div>
                            
                            {eventDate && (
                                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 text-left mt-6">
                                    <h4 className="font-bold text-gray-800 mb-4 uppercase tracking-wider text-xs text-center">Add to your calendar</h4>
                                    <div className="flex flex-col gap-3">
                                        <a href={links.google} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 bg-white border border-gray-200 py-3 px-4 rounded-lg font-bold text-gray-700 hover:bg-gray-50 transition shadow-sm">
                                            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/><path fill="none" d="M1 1h22v22H1z"/></svg>
                                            Google Calendar
                                        </a>
                                        <button onClick={handleIcsClick} className="flex items-center justify-center gap-3 bg-white border border-gray-200 py-3 px-4 rounded-lg font-bold text-gray-700 hover:bg-gray-50 transition shadow-sm">
                                            <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 24 24"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.3.87.78 0 2.26-1.07 3.81-.91 1.65.17 3.05.8 3.94 2.1-3.21 1.9-2.74 6.47.53 7.8-.73 1.1-1.46 2.17-2.43 3.51M12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25"/></svg>
                                            Apple / Outlook
                                        </button>
                                        <a href={links.yahoo} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 bg-white border border-gray-200 py-3 px-4 rounded-lg font-bold text-gray-700 hover:bg-gray-50 transition shadow-sm">
                                            <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 24 24"><path d="M22.46 4.98c-.1-.13-.26-.2-.42-.2H18.8c-.28 0-.48.26-.39.52.88 2.55 1.5 5.56 1.5 8.7 0 2.15-.31 4.19-.88 6.07-.09.28.12.56.41.56h3.19c.16 0 .32-.07.41-.21A14.93 14.93 0 0 0 24 14c0-3.23-.55-6.3-1.54-9.02zM12 24c-1.35 0-2.65-.18-3.88-.51-.27-.08-.55.07-.63.35-.38 1.34-1.22 2.45-2.36 3.19-.2.13-.17.43.05.5A11.96 11.96 0 0 0 12 24zm-6.68-1.57c.21-.19.25-.5.06-.72A11.95 11.95 0 0 1 1.5 14c0-3.3 1.34-6.28 3.5-8.5.18-.19.16-.49-.03-.66l-2.44-2.1c-.16-.14-.41-.12-.55.04C.67 5.43 0 9.54 0 14c0 3.86 1.56 7.36 4.1 9.88.16.16.4.15.54-.01l.68-1.44zm6.68-20.43c1.35 0 2.65.18 3.88.51.27.07.55-.08.63-.35.38-1.34 1.22-2.45 2.36-3.19.2-.13.17-.43-.05-.5A11.96 11.96 0 0 0 12 0zm6.68 1.57c-.21.19-.25.5-.06.72A11.95 11.95 0 0 1 22.5 10c0 3.3-1.34 6.28-3.5 8.5-.18.19-.16.49.03.66l2.44 2.1c.16.14.41.12.55-.04A14.93 14.93 0 0 0 24 10c0-3.86-1.56-7.36-4.1-9.88-.16-.16-.4-.15-.54.01l-.68 1.44zM12 4.5c-5.24 0-9.5 4.26-9.5 9.5s4.26 9.5 9.5 9.5 9.5-4.26 9.5-9.5-4.26-9.5-9.5-9.5zm0 17C7.86 21.5 4.5 18.14 4.5 14S7.86 6.5 12 6.5 19.5 9.86 19.5 14 16.14 21.5 12 21.5z"/></svg>
                                            Yahoo Calendar
                                        </a>
                                    </div>
                                </div>
                            )}
                            
                            <button onClick={onClose} className="mt-6 text-gray-500 hover:text-gray-900 font-bold transition underline text-sm">Return to Website</button>
                        </div>
                    ) : (
                        <>
                            <div className="mb-8">
                                <h3 className="text-2xl font-bold text-gray-900 mb-2 font-serif" style={{ fontFamily }}>Let's stay in touch!</h3>
                                <p className="text-sm text-gray-600">Please provide your mailing address and email so we can send you updates and a formal invitation.</p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">First Name *</label>
                                        <input type="text" name="firstName" required value={formData.firstName} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Last Name *</label>
                                        <input type="text" name="lastName" required value={formData.lastName} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Email Address *</label>
                                        <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Phone Number</label>
                                        <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                    </div>
                                </div>

                                {siteData.allowAdditionalGuests && (
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Additional Guests (+{siteData.maxAdditionalGuests || 1} Max)</label>
                                        <input type="number" name="additionalGuests" min="0" max={siteData.maxAdditionalGuests || 1} value={formData.additionalGuests} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                    </div>
                                )}

                                <div className="pt-3 border-t border-gray-100">
                                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">Mailing Address (For Formal Invite)</label>
                                    <div className="space-y-2">
                                        <input type="text" name="address1" placeholder="Street Address" value={formData.address1} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                        <input type="text" name="address2" placeholder="Apt, Suite, Unit (optional)" value={formData.address2} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                        <div className="grid grid-cols-3 gap-2">
                                            <input type="text" name="city" placeholder="City" value={formData.city} onChange={handleChange} className="col-span-3 sm:col-span-1 p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                            <input type="text" name="state" placeholder="State" value={formData.state} onChange={handleChange} className="col-span-1 p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                            <input type="text" name="zip" placeholder="Zip Code" value={formData.zip} onChange={handleChange} className="col-span-2 sm:col-span-1 p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:bg-white outline-none transition text-sm" style={{ '--tw-ring-color': primaryColor }} />
                                        </div>
                                    </div>
                                </div>
                                
                                <SmsComplianceCheckboxes />

                                <div className="pt-4 flex flex-col gap-2">
                                    <button type="submit" disabled={loading} className="w-full py-4 rounded-xl font-bold text-white shadow-md hover:opacity-90 transform hover:-translate-y-0.5 transition-all disabled:opacity-50" style={{ backgroundColor: primaryColor }}>
                                        {loading ? 'Saving...' : 'Submit & Save Date'}
                                    </button>
                                    <button type="button" onClick={onClose} className="w-full py-3 rounded-xl font-bold text-gray-500 hover:text-gray-800 transition text-sm">
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}